import * as THREE from 'three';
/* 权威状态全部在模拟内核里；本文件只做投影与输入翻译。
   任何领域可变量出现在这里都是回退 —— 一律经 K.* 走命令。 */
import * as K from './kernel-bridge';
import { initDrawers, revealInspector, collapseDrawers } from './ui-drawers';
import { ROAD_W, initInfrastructure, markInfraDirty, flushInfrastructure,
         rebuildInfraLayers, disposeInfrastructure, countInfra } from './infrastructure';
import { initShell, markDirty } from './shell';
import {
  POOL_CAP, POOLS, buildPool, poolGrow, poolAlloc, poolFree, poolTint, writePoolMatrices,
  initBuildingPool,
} from './building-pool';
import {
  camera, camCtl, resetCam, resetCamNow, focusOn, terrainPick, groundPoint, camUpdate, initCameraRig, _tpV,
} from './camera-rig';
import {
  craterHeight, wallFactor, createRenderRng, lerp, clamp, sstep,
  FLOOR_R, RIM_R, WALL_H, OUTER_H, RIDGE_R0, RIDGE_R1, RIDGE_H, TERR_R,
} from './terrain-profile';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

/* 渲染专用随机源。绝不用于模拟结果 —— 那走内核的 SeededRng。 */
const rnd=createRenderRng(20240);
/* three 类型收窄辅助：把 traverse 的 Object3D 安全视为 Mesh 访问 geometry/material */
const asMesh=(o:THREE.Object3D)=>o as THREE.Mesh & {geometry:THREE.BufferGeometry;material:THREE.Material&Record<string,any>};
/* 工程化过渡：demo 来自 JS 单文件，存在少量动态类型。集中用一个宽松别名吸收，
   待后续按模块（渲染/HUD/建筑/交互）拆分时逐步收敛为精确类型。 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj=any;

/* ================= RENDERER ================= */
const container=document.getElementById('scene');
const renderer=new THREE.WebGLRenderer({antialias:true});
/* 渲染倍率：MSAA 只抗几何边缘，管不了细亮元素（道路线/窗带/自发光条）
   在着色器内部产生的亚像素闪烁。DPR=1 的屏幕没有任何超采样余量，
   这类爬行会一直存在。故在总像素预算内向上补一点超采样；
   DPR≥1.5 的屏本身已有余量，不再叠加。
   注意与下方 AA_SAMPLES 的联动：倍率提高后绘制缓冲变大，
   采样数会自动降一档，两者合起来仍在同一开销区间。 */
const _dpr=Math.min(devicePixelRatio,2);
const _SS_MAX_PX=3.3e6;                       // 绘制缓冲像素上限
const _ssCap=Math.sqrt(_SS_MAX_PX/(innerWidth*innerHeight));
renderer.setPixelRatio(_dpr<1.5?Math.max(_dpr,Math.min(_dpr*1.5,_ssCap)):_dpr);
renderer.setSize(innerWidth,innerHeight);
renderer.shadowMap.enabled=true; renderer.shadowMap.type=THREE.PCFSoftShadowMap;
/* 阴影按需重画：场景几乎全静态，太阳位置也只在 gameMin 跳变时移动
   （每 0.5s 一次），默认的每帧全量 shadow pass 是纯浪费 ——
   它占了整帧提交数的将近一半。改为脏标记：时钟跳变/建筑增删时才重画。 */
renderer.shadowMap.autoUpdate=false;
function shadowDirty(){renderer.shadowMap.needsUpdate=true;}
renderer.toneMapping=THREE.ACESFilmicToneMapping; renderer.toneMappingExposure=1.18;
container.appendChild(renderer.domElement);

const scene=new THREE.Scene();
scene.background=new THREE.Color(0x030910);                 // 兜底色；实际由天穹 skyDome 覆盖
// 雾密度随昼夜变化（updateDayNight）：白天薄、夜晚厚，用来把远景山脊压进大气
scene.fog=new THREE.FogExp2(0x0a1a28,0.00042);
/* 相机装置已抽到 ./camera-rig：它只负责移动相机。
   下面这段键盘策略留在这里 —— Esc 要区分「退出放置模式 / 关闭建筑详情 / 回中」，
   R 要旋转放置预览，这些都是应用级语义，相机装置不该知道。 */
const canvas=renderer.domElement;
initCameraRig(canvas);   // canvas 就绪后再注册相机装置的指针监听
addEventListener('keydown',e=>{
  camCtl.keys[e.code]=true;
  /* Esc 是一条【逐级退出】的阶梯，每按一次只退一级：
       放置模式 → 工务工具 → 选中的建筑 → 暂停菜单
     前两级在本文件末尾的 capture 阶段处理，这里处理第三级。

     两处历史行为已修正：
     1) 没有可退的东西时会 resetCam() —— 玩家按 Esc 是想暂停，不是想重置视角，
        视角回中另有 Home / 空格两个键。
     2) 关闭信息卡后没有阻止传播，shell 的 Esc 监听会【同时】打开暂停菜单，
        等于一次按键退了两级。 */
  if(e.code==='Escape'){
    if(window.__placingMode)return;                    // 归放置模式自己处理
    if(window.__cz&&window.__cz.selected&&window.__cz.selected()){
      document.getElementById('biClose').click();
      e.preventDefault();e.stopImmediatePropagation(); // 别再顺手打开暂停菜单
      return;
    }
    return;                                            // 无可退 → 交给 shell 开暂停菜单
  }
  if(e.code==='Space'){resetCam();e.preventDefault();}
  // 放置模式下 R 旋转预览朝向
  if(e.code==='KeyR'&&window.__placingMode&&window.__rotateGhost)window.__rotateGhost();
});
addEventListener('keyup',e=>{camCtl.keys[e.code]=false;});

/* post — 科幻：强辉光 + 冷色调
   ⚠ WebGLRenderer 的 antialias:true 在接了 EffectComposer 之后【完全失效】：
   场景不再画进默认帧缓冲，而是画进 composer 自建的 RenderTarget，
   而 three 建的那个 target 默认 samples:0，没有任何多重采样。
   这就是建筑边缘全是锯齿、抬高视角后更明显的原因。
   解法：显式给 composer 一个带 MSAA 的 target（WebGL2 支持，
   composer.setSize 会保留该选项，无需在 resize 里重建）。 */
// 采样数按实际像素量分档：MSAA 的开销正比于 像素×采样数，
// 1080p 级别可以放心开 8×，4K/DPR2（约 830 万像素）就必须降到 2×。
const _rtSize=renderer.getDrawingBufferSize(new THREE.Vector2());
const _px=_rtSize.x*_rtSize.y;
const AA_SAMPLES=Math.min(
  renderer.capabilities.maxSamples ?? 4,
  _px>5.0e6 ? 2 : _px>2.6e6 ? 4 : 8
);
const _composerRT=new THREE.WebGLRenderTarget(_rtSize.x,_rtSize.y,{
  type:THREE.HalfFloatType,        // 辉光需要 HDR，与 three 的默认一致
  samples:AA_SAMPLES,
});
_composerRT.texture.name='EffectComposer.rt1';
const composer=new EffectComposer(renderer,_composerRT);
composer.addPass(new RenderPass(scene,camera));
const bloom=new UnrealBloomPass(new THREE.Vector2(innerWidth,innerHeight),0.75,.4,.5);
composer.addPass(bloom);

/* ================= LIGHTS —— 冷暖对比科幻布光 ================= */
const amb=new THREE.AmbientLight(0x2c3e58,1.7); scene.add(amb);      // 冷蓝环境底
const hemi=new THREE.HemisphereLight(0x8fb8e8,0x2a1f18,.85);       // 天光冷蓝 / 地光暖棕
scene.add(hemi);
window.__lights={amb,hemi}; // sun/rim/rim2 assigned below for day-night art review
const sun=new THREE.DirectionalLight(0xffd2a0,1.55);
sun.position.set(360,520,220); sun.castShadow=true;
sun.shadow.mapSize.set(2048,2048);
Object.assign(sun.shadow.camera,{left:-650,right:650,top:650,bottom:-650,far:1800});
sun.shadow.bias=-0.0005;
scene.add(sun);
const rim=new THREE.DirectionalLight(0x3fc8ff,.95); rim.position.set(-320,220,-360); scene.add(rim); // 强青色轮廓光
const rim2=new THREE.DirectionalLight(0x2a6cff,.5); rim2.position.set(320,140,-260); scene.add(rim2); // 补一道蓝光，拉开层次
window.__lights.sun=sun; window.__lights.rim=rim; window.__lights.rim2=rim2; // for day/night art review
const coreGlow=new THREE.PointLight(0xff8a35,2600,700,1.7); coreGlow.position.set(0,80,0); scene.add(coreGlow);
const coreGlow2=new THREE.PointLight(0xffc060,1200,360,2); coreGlow2.position.set(0,26,0); scene.add(coreGlow2);
// 城市级青色科技泛光（自坑底向上，给建筑勾冷边）
const cityGlow=new THREE.PointLight(0x36c8ff,900,560,1.9); cityGlow.position.set(0,34,0); scene.add(cityGlow);

/* ================= PROCEDURAL TEXTURES ================= */
/* 地表岩石：旧版是 512px 噪点铺 8 次覆盖 1150m —— 每米不到 4 像素，
   放大后只剩一片均匀的棕色糊。改为 1024px 多尺度噪声，UV 按世界坐标
   每 56m 一铺（约 18 px/m），并由同一张高度图生成法线贴图，
   让地面在掠射光下有真实的凹凸，而不是一张平板。 */
const ROCK_TILE=56;                       // 一张贴图覆盖的米数
function _rockCanvases(){
  const S=1024;
  const col=document.createElement('canvas'); col.width=col.height=S;
  const hei=document.createElement('canvas'); hei.width=hei.height=S;
  const cx=col.getContext('2d')!, hx=hei.getContext('2d')!;
  cx.fillStyle='#bcb3a8'; cx.fillRect(0,0,S,S);
  hx.fillStyle='#808080'; hx.fillRect(0,0,S,S);
  // 三个尺度：大石斑 -> 碎石 -> 细砂。缺了大尺度就没有「地质感」，
  // 只有细砂会在远处退化成均匀噪声。
  const layers=[[70,26,52,.44],[420,9,20,.38],[2600,2,6,.30]];
  for(const [n,rmin,rmax,al] of layers as [number,number,number,number][]){
    for(let i=0;i<n;i++){
      const x=rnd()*S,y=rnd()*S,r=rmin+rnd()*(rmax-rmin);
      const v=rnd(), warm=v*0.5+0.5;
      cx.fillStyle=`rgba(${(118+warm*126)|0},${(108+warm*120)|0},${(98+warm*112)|0},${al})`;
      cx.beginPath(); cx.ellipse(x,y,r,r*(.6+rnd()*.7),rnd()*6.28,0,6.28); cx.fill();
      const g=(128+(v-.5)*150)|0;
      hx.fillStyle=`rgba(${g},${g},${g},${al*1.5})`;
      hx.beginPath(); hx.ellipse(x,y,r,r*(.6+rnd()*.7),rnd()*6.28,0,6.28); hx.fill();
    }
  }
  // 撞击坑麻点：陨坑地表必然布满小撞击坑，这是月/陨石地貌的识别特征
  for(let i=0;i<260;i++){
    const x=rnd()*S,y=rnd()*S,r=3+rnd()*13;
    const g=cx.createRadialGradient(x,y,r*.2,x,y,r);
    g.addColorStop(0,'rgba(96,88,80,.55)'); g.addColorStop(.7,'rgba(140,131,121,.25)');
    g.addColorStop(1,'rgba(226,214,198,.18)');
    cx.fillStyle=g; cx.beginPath(); cx.arc(x,y,r,0,6.28); cx.fill();
    const hg=hx.createRadialGradient(x,y,r*.2,x,y,r);
    hg.addColorStop(0,'rgba(40,40,40,.8)'); hg.addColorStop(.75,'rgba(120,120,120,.4)');
    hg.addColorStop(1,'rgba(210,210,210,.35)');
    hx.fillStyle=hg; hx.beginPath(); hx.arc(x,y,r,0,6.28); hx.fill();
  }
  return {col,hei,S};
}
/* 由灰度高度图求法线：Sobel 差分 -> 切线空间法线。
   尺寸必须是 2 的幂，下面用 &(S-1) 做无缝环绕取样。 */
function heightToNormal(hei:HTMLCanvasElement,S:number,strength:number){
  const src=hei.getContext('2d')!.getImageData(0,0,S,S).data;
  const out=document.createElement('canvas'); out.width=out.height=S;
  const o=out.getContext('2d')!, img=o.createImageData(S,S);
  const H=(x:number,y:number)=>src[(((y&(S-1))*S)+(x&(S-1)))*4]/255;
  for(let y=0;y<S;y++)for(let x=0;x<S;x++){
    const dx=(H(x+1,y)-H(x-1,y))*strength, dy=(H(x,y+1)-H(x,y-1))*strength;
    const l=Math.hypot(-dx,-dy,1), i=(y*S+x)*4;
    img.data[i]=((-dx/l)*.5+.5)*255; img.data[i+1]=((-dy/l)*.5+.5)*255;
    img.data[i+2]=((1/l)*.5+.5)*255; img.data[i+3]=255;
  }
  o.putImageData(img,0,0); return out;
}
function rockMaps(){
  const {col,hei,S}=_rockCanvases();
  const maxA=renderer.capabilities.getMaxAnisotropy();
  const mk=(cv:HTMLCanvasElement,srgb:boolean)=>{
    const t=new THREE.CanvasTexture(cv);
    t.wrapS=t.wrapT=THREE.RepeatWrapping;
    t.anisotropy=maxA;                      // 掠射角下不加各向异性会糊成条带
    if(srgb)t.colorSpace=THREE.SRGBColorSpace;
    return t;
  };
  return {map:mk(col,true),normalMap:mk(heightToNormal(hei,S,4.6),false)};
}
function windowTexture(warm){
  const c=document.createElement('canvas');c.width=64;c.height=128;const x=c.getContext('2d');
  x.fillStyle='#141b23';x.fillRect(0,0,64,128);
  const cols=5,rows=15,cw=64/cols,ch=128/rows;
  for(let r=0;r<rows;r++)for(let col=0;col<cols;col++){
    if(rnd()<.5){const b=.45+rnd()*.55;
      x.fillStyle=warm?`rgba(255,${165+rnd()*70|0},${85+rnd()*70|0},${b})`:`rgba(${120+rnd()*90|0},${205+rnd()*45|0},255,${b})`;
      x.fillRect(col*cw+1.5,r*ch+2,cw-3,ch-4);}}
  return new THREE.CanvasTexture(c);
}
const warmWin=windowTexture(true), coolWin=windowTexture(false);

/* ================= CRATER TERRAIN (reliable radial profile) ================= */

/* 极坐标网格而不是方形平面：陨坑本身就是径向结构，密度按半径分配 ——
   坑底/坑壁每 7m 一格，远景山脊每 70m 一格。用比旧版更少的顶点覆盖 7.6 倍范围。
   UV 直接取世界坐标（除以 ROCK_TILE），因此不存在极点处的贴图拉伸。 */
const terrGeo=new THREE.BufferGeometry();
{
  const RINGS:number[]=[];
  for(let r=0;r<=980;r+=7) RINGS.push(r);            // 坑底 + 坑壁 + 外坡：细
  for(let r=1002;r<=2100;r+=22) RINGS.push(r);       // 外缘平原：中
  for(let r=2170;r<=TERR_R;r+=70) RINGS.push(r);     // 远景山脊：粗
  const SEC=192, NR=RINGS.length;
  const pos=new Float32Array(NR*SEC*3), uv=new Float32Array(NR*SEC*2),
        colArr=new Float32Array(NR*SEC*3);
  // 反照率参考月壤/玄武岩（约 0.10~0.16），贴图只提供细节不再自带暗色
  const cFloor=new THREE.Color(0x4d3d2f), cWall=new THREE.Color(0x806042),
        cRock=new THREE.Color(0x5d4632), cPlain=new THREE.Color(0x453729),
        cFar=new THREE.Color(0x3d352f);
  const tmp=new THREE.Color();
  for(let i=0;i<NR;i++){
    const r=RINGS[i];
    for(let s=0;s<SEC;s++){
      const a=s/SEC*Math.PI*2;
      const X=Math.cos(a)*r, Z=Math.sin(a)*r, Y=craterHeight(X,Z);
      const k=(i*SEC+s);
      pos[k*3]=X; pos[k*3+1]=Y; pos[k*3+2]=Z;
      uv[k*2]=X/ROCK_TILE; uv[k*2+1]=Z/ROCK_TILE;
      const wf=wallFactor(X,Z);
      const band=Math.sin(Y*.09)*.5+.5;                       // 坑壁层理
      tmp.copy(cFloor).lerp(cWall,wf).lerp(cRock,band*wf*.5);
      // 宏观斑块（约 90m/30m 两个尺度）：战略视距下贴图被 mipmap 抹平，
      // 地表明暗必须由顶点色承载，否则拉远后读作一张纯色纸。
      const m1=Math.sin(X*.011+Z*.017)*Math.sin(X*.0138-Z*.0072);
      const m2=Math.sin(X*.043-Z*.031)*Math.sin(X*.029+Z*.047);
      tmp.multiplyScalar(1+m1*.17+m2*.11);
      tmp.r*=1+m1*.05;  tmp.b*=1-m1*.045;                     // 斑块间轻微冷暖漂移
      if(r>RIM_R){                                            // 坑外逐渐转冷、去饱和
        tmp.lerp(cPlain,Math.min((r-RIM_R)/700,1)*.85);
        if(r>RIDGE_R0) tmp.lerp(cFar,Math.min((r-RIDGE_R0)/1500,1)*.8);
      }
      colArr[k*3]=tmp.r; colArr[k*3+1]=tmp.g; colArr[k*3+2]=tmp.b;
    }
  }
  const idx:number[]=[];
  for(let i=0;i<NR-1;i++)for(let s=0;s<SEC;s++){
    const s2=(s+1)%SEC;                                       // 闭合，无接缝
    const a=i*SEC+s, b=i*SEC+s2, c=(i+1)*SEC+s2, d=(i+1)*SEC+s;
    // 缠绕必须是 a-b-c / a-c-d：反过来（a-c-b）法线朝下，
    // FrontSide 剔除会让整块地形从上方完全不可见。
    idx.push(a,b,c, a,c,d);
  }
  terrGeo.setAttribute('position',new THREE.BufferAttribute(pos,3));
  terrGeo.setAttribute('uv',new THREE.BufferAttribute(uv,2));
  terrGeo.setAttribute('color',new THREE.BufferAttribute(colArr,3));
  terrGeo.setIndex(idx);
  terrGeo.computeVertexNormals();
}
const _rock=rockMaps();
const terrain=new THREE.Mesh(terrGeo,new THREE.MeshStandardMaterial({
  map:_rock.map, normalMap:_rock.normalMap, vertexColors:true,
  roughness:.96, metalness:.02}));
terrain.material.normalScale.set(1.7,1.7);
terrain.receiveShadow=true; scene.add(terrain);

/* 散落巨石：外缘平原空得发假。用 InstancedMesh 一次绘制，几乎零开销。 */
{
  const N=720, g=new THREE.IcosahedronGeometry(1,0);
  const rocks=new THREE.InstancedMesh(g,new THREE.MeshStandardMaterial({
    color:0x51443a,roughness:.98,metalness:.02,flatShading:true}),N);
  const m=new THREE.Matrix4(), q=new THREE.Quaternion(), e=new THREE.Euler(),
        v=new THREE.Vector3(), sc=new THREE.Vector3();
  for(let i=0;i<N;i++){
    const a=rnd()*Math.PI*2;
    // 只撒在坑外：坑内是建成区，撒石头会和建筑打架
    const r=RIM_R+120+Math.pow(rnd(),.7)*(TERR_R-RIM_R-260);
    const x=Math.cos(a)*r, z=Math.sin(a)*r;
    const s=(2.2+rnd()*7)*(1+r/TERR_R*2.6);          // 远处的放大，保持视觉粒度一致
    v.set(x,craterHeight(x,z)+s*.35,z);
    e.set(rnd()*6.28,rnd()*6.28,rnd()*6.28); q.setFromEuler(e);
    sc.set(s,s*(.5+rnd()*.5),s*(.7+rnd()*.6));
    rocks.setMatrixAt(i,m.compose(v,q,sc));
  }
  rocks.instanceMatrix.needsUpdate=true; rocks.receiveShadow=true; rocks.castShadow=true;
  scene.add(rocks);
}

/* ================= 天穹 + 星空 + 昼夜循环 =================
   HUD 一直在走时钟（第 N 天 / 时:分），但场景光照是写死的，任何时刻都是夜。
   这里把光照、雾、天色、星空全部挂到 gameMin 上。
   天穹与星空跟随相机平移（经典做法），因此半径只要 7000 即可，
   永远不会被 far=11000 裁到。 */
const skyGrp=new THREE.Group(); scene.add(skyGrp);
const skyUni={
  uTop:{value:new THREE.Color(0x0a1526)},
  uHorizon:{value:new THREE.Color(0x16283a)},
  uSunDir:{value:new THREE.Vector3(0,1,0)},
  uSunCol:{value:new THREE.Color(0xffd2a0)},
  uSunI:{value:0.0},
};
const skyDome=new THREE.Mesh(
  new THREE.SphereGeometry(7000,32,20),
  new THREE.ShaderMaterial({
    side:THREE.BackSide, depthWrite:false, uniforms:skyUni,
    vertexShader:`varying vec3 vDir;
      void main(){ vDir=position; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader:`
      varying vec3 vDir;
      uniform vec3 uTop,uHorizon,uSunCol,uSunDir; uniform float uSunI;
      void main(){
        vec3 d=normalize(vDir);
        float h=clamp(d.y*0.5+0.5,0.0,1.0);
        vec3 col=mix(uHorizon,uTop,pow(h,0.72));
        float s=max(dot(d,uSunDir),0.0);
        col+=uSunCol*pow(s,180.0)*uSunI*2.2;    // 日轮
        col+=uSunCol*pow(s,7.0)*uSunI*0.30;     // 近日大气辉光
        col+=uSunCol*pow(s,1.6)*uSunI*0.07;     // 全天散射
        gl_FragColor=vec4(col,1.0);
      }`,
  })
);
skyDome.renderOrder=-1; skyGrp.add(skyDome);

/* 星空：只在夜间可见，白天由 opacity 淡出 */
const starMat=new THREE.PointsMaterial({
  color:0xdfe9ff,size:2.4,sizeAttenuation:false,transparent:true,opacity:1,depthWrite:false});
{
  const N=2200, p=new Float32Array(N*3);
  for(let i=0;i<N;i++){
    // 只铺上半球，地平线以下看不到
    const u=rnd()*Math.PI*2, v=Math.acos(rnd()*0.98+0.02), R=6600;
    p[i*3]=Math.sin(v)*Math.cos(u)*R; p[i*3+1]=Math.cos(v)*R; p[i*3+2]=Math.sin(v)*Math.sin(u)*R;
  }
  const g=new THREE.BufferGeometry(); g.setAttribute('position',new THREE.BufferAttribute(p,3));
  const stars=new THREE.Points(g,starMat); stars.renderOrder=-1; skyGrp.add(stars);
}

/* 月光：太阳落下后接管，冷蓝、弱、方向与太阳相反 */
const moon=new THREE.DirectionalLight(0xa8c8ff,0.0); scene.add(moon);
moon.castShadow=true; moon.shadow.mapSize.set(1024,1024);
Object.assign(moon.shadow.camera,{left:-650,right:650,top:650,bottom:-650,far:1800});
moon.shadow.bias=-0.0006;
window.__lights.moon=moon;

const _sunDir=new THREE.Vector3(), _up=new THREE.Vector3();
const C_NOON=new THREE.Color(0xfff2dc), C_GOLD=new THREE.Color(0xff9a4e);
const C_SKY_DAY=new THREE.Color(0x2f6ea8), C_SKY_NIGHT=new THREE.Color(0x0a1526);
const C_HOR_DAY=new THREE.Color(0x9fb0b4), C_HOR_DUSK=new THREE.Color(0xc4642a),
      C_HOR_NIGHT=new THREE.Color(0x16283a);
const C_FOG_DAY=new THREE.Color(0x8a9096), C_FOG_NIGHT=new THREE.Color(0x0a1a28);
const C_AMB_DAY=new THREE.Color(0x8296a8), C_AMB_NIGHT=new THREE.Color(0x2c3e58);
const _c1=new THREE.Color(), _c2=new THREE.Color();
const PHASES:[number,string][]=[[5,'深夜'],[7,'黎明'],[10,'清晨'],[13,'正午'],
                                [16,'午后'],[18,'日落'],[20,'黄昏'],[24,'夜晚']];
const phaseEl=document.getElementById('phase');
// 美术评审钩子（同 window.__lights）：__czTime(13*60) 直接跳到正午
window.__czTime=(m:number)=>{gameMin=((m%1440)+1440)%1440;updateDayNight(gameMin);
  clockEl.textContent=`${String(Math.floor(gameMin/60)).padStart(2,'0')}:${String(gameMin%60).padStart(2,'0')}`;};

function updateDayNight(min:number){
  // 0:00 -> ang=-π/2（太阳在地平线下最深）；6:00 正东升；12:00 最高；18:00 正西落
  const ang=(min/1440)*Math.PI*2-Math.PI/2;
  const tilt=0.42;                                  // 太阳轨迹相对天顶的倾角，让阴影不是正东西向
  _up.set(0,Math.cos(tilt),Math.sin(tilt));
  _sunDir.set(Math.cos(ang),0,0).addScaledVector(_up,Math.sin(ang)).normalize();
  const elev=_sunDir.y;

  const day=sstep(-0.10,0.20,elev);                 // 0=夜 1=昼
  const gold=Math.max(0,1-Math.abs(elev)/0.28);     // 地平线附近的金色权重

  sun.position.copy(_sunDir).multiplyScalar(900);
  sun.intensity=day*2.55+0.03;
  sun.color.copy(_c1.copy(C_GOLD).lerp(C_NOON,sstep(0.06,0.42,elev)));
  sun.visible=day>0.01;

  moon.position.copy(_sunDir).multiplyScalar(-820);
  moon.intensity=(1-day)*1.15;      // 现实月光远弱于此；夜晚全黑不可玩，按游戏惯例抬高
  moon.visible=day<0.99;
  // 阴影只留主导光源一份：黎明/黄昏两盏都开着投影会让阴影 pass 翻倍，
  // 且两套方向相反的影子叠在一起本来就不好看
  sun.castShadow=day>=0.5; moon.castShadow=day<0.5;

  amb.color.copy(_c1.copy(C_AMB_NIGHT).lerp(C_AMB_DAY,day));
  amb.intensity=2.05-day*1.74;      // 夜 2.05 / 昼 0.31
  hemi.intensity=0.85-day*0.30;     // 夜 0.85 / 昼 0.55
  hemi.color.copy(_c1.copy(C_SKY_NIGHT).lerp(C_SKY_DAY,day));
  // 青/蓝轮廓光是夜景的风格化补光，白天必须收掉，否则正午的岩石泛蓝
  rim.intensity=0.16+(1-day)*0.80;
  rim2.intensity=0.08+(1-day)*0.42;

  _c1.copy(C_HOR_NIGHT).lerp(C_HOR_DAY,day).lerp(C_HOR_DUSK,gold*0.75);
  skyUni.uHorizon.value.copy(_c1);
  skyUni.uTop.value.copy(_c2.copy(C_SKY_NIGHT).lerp(C_SKY_DAY,day));
  skyUni.uSunDir.value.copy(_sunDir);
  skyUni.uSunCol.value.copy(sun.color);
  skyUni.uSunI.value=Math.max(0,sstep(-0.06,0.10,elev));

  starMat.opacity=Math.max(0,1-day*1.35);
  (scene.fog as THREE.FogExp2).color.copy(_c1.copy(C_FOG_NIGHT).lerp(C_FOG_DAY,day));
  (scene.fog as THREE.FogExp2).density=0.00042-day*0.00018;   // 白天通透，夜里压住远景
  (scene.background as THREE.Color).copy(skyUni.uHorizon.value);
  renderer.toneMappingExposure=1.18-day*0.22;

  const hr=min/60;
  const ph=PHASES.find(p=>hr<p[0]) || PHASES[PHASES.length-1];
  if(phaseEl && phaseEl.textContent!==ph[1]) phaseEl.textContent=ph[1];
}

/* ================= CORE WELL (陨核井) ================= */
const core=new THREE.Group(); scene.add(core);
core.userData.bType='core'; core.userData.bId=0;
core.traverse(o=>{o.userData.pickRoot=core;});
/* 熔池光晕 —— 收敛在 GLB 台座内部的一圈暖光，不再是大白盘 */
const molten=new THREE.Mesh(new THREE.CircleGeometry(20,40),new THREE.MeshBasicMaterial({color:0xff8a35,transparent:true,opacity:.5,depthWrite:false}));
molten.rotation.x=-Math.PI/2; molten.position.y=15; core.add(molten);
const molten2=new THREE.Mesh(new THREE.CircleGeometry(13,32),new THREE.MeshBasicMaterial({color:0xffd070,transparent:true,opacity:.55,depthWrite:false}));
molten2.rotation.x=-Math.PI/2; molten2.position.y=15.4; core.add(molten2);
const molten3=new THREE.Mesh(new THREE.CircleGeometry(7,24),new THREE.MeshBasicMaterial({color:0xfff0b0,transparent:true,opacity:.6,depthWrite:false}));
molten3.rotation.x=-Math.PI/2; molten3.position.y=15.8; core.add(molten3);

/* 能量射流 —— 从堆芯升起，细而聚拢，底部暖、往上转白 */
const beamOuter=new THREE.Mesh(new THREE.CylinderGeometry(4.5,7.5,190,24,1,true),
  new THREE.MeshBasicMaterial({color:0xff9540,transparent:true,opacity:.12,side:THREE.DoubleSide,depthWrite:false,blending:THREE.AdditiveBlending}));
beamOuter.position.y=130; core.add(beamOuter);
const beamMid=new THREE.Mesh(new THREE.CylinderGeometry(2.6,4.2,190,20,1,true),
  new THREE.MeshBasicMaterial({color:0xffb060,transparent:true,opacity:.22,side:THREE.DoubleSide,depthWrite:false,blending:THREE.AdditiveBlending}));
beamMid.position.y=130; core.add(beamMid);
const beamCore=new THREE.Mesh(new THREE.CylinderGeometry(1.2,1.8,190,18,1,true),
  new THREE.MeshBasicMaterial({color:0xfff0c0,transparent:true,opacity:.5,side:THREE.DoubleSide,depthWrite:false,blending:THREE.AdditiveBlending}));
beamCore.position.y=130; core.add(beamCore);

/* structural rings + pylons */
const coreStruct=[];   // 程序化结构件，CORE GLB 加载后隐藏
const ring1=new THREE.Mesh(new THREE.TorusGeometry(44,4.5,12,64),
  new THREE.MeshStandardMaterial({color:0x8a97a5,metalness:.85,roughness:.28,emissive:0xff7010,emissiveIntensity:.9}));
ring1.rotation.x=Math.PI/2; ring1.position.y=8; ring1.castShadow=true; core.add(ring1); coreStruct.push(ring1);
const ring2=new THREE.Mesh(new THREE.TorusGeometry(58,2.2,10,64),new THREE.MeshStandardMaterial({color:0x6b7683,metalness:.7,roughness:.4}));
ring2.rotation.x=Math.PI/2; ring2.position.y=4; core.add(ring2); coreStruct.push(ring2);
for(let i=0;i<10;i++){
  const a=i/10*Math.PI*2;
  const py=new THREE.Mesh(new THREE.CylinderGeometry(1.8,2.8,64,8),new THREE.MeshStandardMaterial({color:0x7c8894,metalness:.7,roughness:.35}));
  py.position.set(Math.cos(a)*54,32,Math.sin(a)*54); py.castShadow=true; core.add(py); coreStruct.push(py);
  const tip=new THREE.Mesh(new THREE.SphereGeometry(2.6,10,10),new THREE.MeshBasicMaterial({color:0xffcf80}));
  tip.position.set(Math.cos(a)*54,66,Math.sin(a)*54); core.add(tip); coreStruct.push(tip);
  const strut=new THREE.Mesh(new THREE.BoxGeometry(16,1.2,2),new THREE.MeshStandardMaterial({color:0x5a646f,metalness:.6,roughness:.5}));
  strut.position.set(Math.cos(a)*46,54,Math.sin(a)*46); strut.rotation.y=-a; core.add(strut); coreStruct.push(strut);
}

/* rising embers —— 从堆芯口升起的一缕 */
const emberN=300, emberPos=new Float32Array(emberN*3), emberV=new Float32Array(emberN);
for(let i=0;i<emberN;i++){const a=rnd()*Math.PI*2,r=rnd()*12;
  emberPos[i*3]=Math.cos(a)*r; emberPos[i*3+1]=35+rnd()*170; emberPos[i*3+2]=Math.sin(a)*r; emberV[i]=.4+rnd()*1.1;}
const emberGeo=new THREE.BufferGeometry(); emberGeo.setAttribute('position',new THREE.BufferAttribute(emberPos,3));
const embers=new THREE.Points(emberGeo,new THREE.PointsMaterial({color:0xffb060,size:1.8,transparent:true,opacity:.9,depthWrite:false,blending:THREE.AdditiveBlending}));
core.add(embers);

/* 真实 CORE GLB 地标：加载陨核反应井模型作为底座结构，替换上面的简易 ring/pylon。
   保留体积光束(beam)与余烬粒子(embers)这两个 GLB 没有的特效。 */
const coreAnim={holo:null,coreMat:null,wellMat:null,plasmaMat:null,cyanHiMat:null,spinPivot:null};
new GLTFLoader().load('models/building.core.mk1.glb',gltf=>{
  const m=gltf.scene;
  m.traverse(o=>{if((o as AnyObj).isMesh){o.castShadow=true;o.receiveShadow=true;}});
  m.position.y=0; core.add(m);
  m.traverse(o=>{o.userData.pickRoot=core;});
  // GLB 已含同心环+塔柱，隐藏程序化结构件，仅留熔池/光束/余烬
  coreStruct.forEach(o=>o.visible=false);

  /* ---- 科幻动画：克隆全息环为独立旋转节点（同心于陨核），并引用发光材质做脉冲 ----
     克隆几何+克隆材质，不影响共享材质与其他建筑。 */
  m.traverse(o=>{
    if(!(o as AnyObj).isMesh||!o.name)return;
    const short=o.name.replace('CZ_CORE_','');
    if(short==='Core')coreAnim.coreMat=asMesh(o).material;
    else if(short==='WellGlow')coreAnim.wellMat=asMesh(o).material;
    else if(short==='Plasma')coreAnim.plasmaMat=asMesh(o).material;
    else if(short==='CyanHi')coreAnim.cyanHiMat=asMesh(o).material;
    else if(short==='Holo'){
      // 陨核中心（几何在模型局部空间，核球心约在 CORE_Z+2 = 24m 高处）
      const CORE=new THREE.Vector3(0,24,0);
      // 以核为轴心的枢轴；全息环几何克隆进来并平移到以核为原点
      const pivot=new THREE.Group(); pivot.position.copy(CORE); m.add(pivot);
      const ng=asMesh(o).geometry.clone(); ng.translate(-CORE.x,-CORE.y,-CORE.z);
      const mat=asMesh(o).material.clone(); mat.transparent=true; mat.opacity=.6; mat.depthWrite=false;
      const holo=new THREE.Mesh(ng,mat); pivot.add(holo);
      coreAnim.spinPivot=pivot; coreAnim.holo=mat;
      o.visible=false; // 用可旋转的克隆节点替代原静态全息网格
    }
  });
  window.__coreGLB=true as AnyObj;
},undefined,()=>{window.__coreGLB=false as AnyObj; /* 缺失则保留程序化结构 */});

/* ambient dust */
const dustN=800, dustPos=new Float32Array(dustN*3);
for(let i=0;i<dustN;i++){const a=rnd()*Math.PI*2,r=rnd()*RIM_R;
  dustPos[i*3]=Math.cos(a)*r; dustPos[i*3+1]=craterHeight(Math.cos(a)*r,Math.sin(a)*r)+rnd()*120+8; dustPos[i*3+2]=Math.sin(a)*r;}
const dustGeo=new THREE.BufferGeometry(); dustGeo.setAttribute('position',new THREE.BufferAttribute(dustPos,3));
const dust=new THREE.Points(dustGeo,new THREE.PointsMaterial({color:0x8fa8bd,size:.9,transparent:true,opacity:.22,depthWrite:false}));
scene.add(dust);

/* ================= CITY — distinct per-type silhouettes ================= */
const city=new THREE.Group(); scene.add(city);
initInfrastructure({city,shadowDirty});   // 基建层挂在 city 下
const steel=new THREE.MeshStandardMaterial({color:0x7a8592,metalness:.75,roughness:.35});
const darkSteel=new THREE.MeshStandardMaterial({color:0x49525c,metalness:.7,roughness:.45});
const hullA=new THREE.MeshStandardMaterial({color:0xa8b6c2,metalness:.6,roughness:.42});
const hullB=new THREE.MeshStandardMaterial({color:0x8b98a6,metalness:.6,roughness:.45});
const hullC=new THREE.MeshStandardMaterial({color:0xb6a894,metalness:.5,roughness:.5});
const baseSkirt=new THREE.MeshStandardMaterial({color:0x333a42,metalness:.6,roughness:.6});
/* 材质缓存：这两个工厂原本每次调用都 new 一个材质，而每栋程序化建筑要调用多次，
   导致 2070 个网格对应 1500 个独立材质 —— 每个材质是一次独立的着色器绑定，
   draw call 无法合并。按参数做键缓存后，同参数复用同一实例。 */
const _winCache=new Map(), _glowCache=new Map();
function winMat(warm,intensity){
  const k=(warm?'w':'c')+':'+intensity;
  let m=_winCache.get(k);
  if(!m){m=new THREE.MeshStandardMaterial({color:0x9fb2c2,metalness:.55,roughness:.45,
    emissiveMap:warm?warmWin:coolWin,emissive:0xffffff,emissiveIntensity:intensity});
    _winCache.set(k,m);}
  return m;
}
function glowMat(c){
  let m=_glowCache.get(c);
  if(!m){m=new THREE.MeshStandardMaterial({color:0x222a30,metalness:.4,roughness:.5,
    emissive:c,emissiveIntensity:1.4}); _glowCache.set(c,m);}
  return m;
}
let bid=0;
function box(w,h,d,mat,x=0,y=0,z=0){const m=new THREE.Mesh(new THREE.BoxGeometry(w,h,d),mat);
  m.position.set(x,y,z);m.castShadow=true;m.receiveShadow=true;return m;}
function cyl(rt,rb,h,mat,x=0,y=0,z=0,seg=12){const m=new THREE.Mesh(new THREE.CylinderGeometry(rt,rb,h,seg),mat);
  m.position.set(x,y,z);m.castShadow=true;return m;}
function sph(r,mat,x=0,y=0,z=0){const m=new THREE.Mesh(new THREE.SphereGeometry(r,14,12),mat);
  m.position.set(x,y,z);m.castShadow=true;return m;}

/* --- per-type builders: each returns a Group sitting on y=0 ground --- */
const TYPES={
  habitat(s){const g=new THREE.Group(),warm=rnd()<.8; // 密封舱 + 廊桥
    const n=2+Math.floor(rnd()*2);
    for(let i=0;i<n;i++){const a=i/n*Math.PI*2,r=7*s;
      const dome=new THREE.Mesh(new THREE.SphereGeometry(6*s,16,12,0,Math.PI*2,0,Math.PI/2),winMat(warm,1.5));
      dome.position.set(Math.cos(a)*r,0,Math.sin(a)*r);dome.castShadow=true;dome.scale.y=.8;g.add(dome);
      g.add(cyl(6*s,6.5*s,2*s,baseSkirt,Math.cos(a)*r,1*s,Math.sin(a)*r));}
    g.add(box(4*s,3*s,14*s,hullB,0,1.5*s,0)); // connecting spine
    return g;},
  industrial(s){const g=new THREE.Group(); // 厂房 + 双烟囱
    g.add(box(16*s,9*s,12*s,winMat(rnd()<.6,1.1),0,4.5*s,0));
    for(let i=-1;i<=1;i+=2){g.add(cyl(1.6*s,2*s,16*s,darkSteel,i*6*s,17*s,0));
      g.add(cyl(2.2*s,2.2*s,1.5*s,glowMat(0xff5030),i*6*s,25.5*s,0));}
    g.add(box(18*s,2*s,14*s,baseSkirt,0,1*s,0));
    return g;},
  oxygen(s){const g=new THREE.Group(); // 过滤塔群 + 膜组
    for(let i=0;i<3;i++){const x=(i-1)*7*s;
      g.add(cyl(2.6*s,3*s,(14+i*4)*s,hullA,x,(7+i*2)*s,0));
      g.add(cyl(3.4*s,2.6*s,2*s,glowMat(0x59d6ff),x,(14+i*4)*s+1*s,0));}
    g.add(box(20*s,1.5*s,8*s,baseSkirt,0,.7*s,0));
    return g;},
  power(s){const g=new THREE.Group(); // 储能线圈塔
    g.add(cyl(4*s,5*s,4*s,baseSkirt,0,2*s,0));
    for(let i=0;i<4;i++){const t=new THREE.Mesh(new THREE.TorusGeometry(4.5*s,.8*s,8,20),glowMat(0xffb060));
      t.rotation.x=Math.PI/2;t.position.y=(6+i*4)*s;g.add(t);}
    g.add(cyl(1*s,1*s,24*s,darkSteel,0,12*s,0));
    return g;},
  water(s){const g=new THREE.Group(); // 钻井塔 + 储罐
    const tw=cyl(.8*s,3.5*s,22*s,darkSteel,-6*s,11*s,0,6); tw.rotation.z=.12; g.add(tw);
    g.add(sph(6*s,hullC,5*s,6*s,0));
    g.add(cyl(6*s,6*s,2*s,baseSkirt,5*s,1*s,0));
    g.add(cyl(1.5*s,1.5*s,10*s,glowMat(0x40c8ff),5*s,14*s,0));
    return g;},
  food(s){const g=new THREE.Group(); // 温室穹顶 + 培养塔
    const dome=new THREE.Mesh(new THREE.SphereGeometry(9*s,18,12,0,Math.PI*2,0,Math.PI/2),
      new THREE.MeshStandardMaterial({color:0x9fd8b0,metalness:.2,roughness:.2,transparent:true,opacity:.5,emissive:0x40ff90,emissiveIntensity:.4}));
    dome.position.y=2*s;dome.castShadow=true;g.add(dome);
    g.add(cyl(9*s,9.5*s,2.5*s,baseSkirt,0,1.2*s,0));
    g.add(cyl(2*s,2*s,10*s,hullB,11*s,5*s,0));
    return g;},
  science(s){const g=new THREE.Group(); // 实验室 + 雷达碟
    g.add(box(10*s,8*s,10*s,winMat(false,1.3),0,4*s,0));
    const dish=new THREE.Mesh(new THREE.SphereGeometry(6*s,16,10,0,Math.PI*2,0,Math.PI/3),hullA);
    dish.rotation.x=-Math.PI/2.4;dish.position.y=12*s;g.add(dish);
    g.add(cyl(.6*s,.6*s,8*s,darkSteel,0,10*s,0));
    return g;},
  refinery(s){const g=new THREE.Group(); // 精炼：高塔 + 反应釜
    g.add(cyl(3*s,4*s,26*s,hullB,-5*s,13*s,0));
    g.add(sph(5.5*s,darkSteel,6*s,6*s,0));
    g.add(cyl(2*s,2*s,4*s,glowMat(0xff7030),-5*s,27*s,0));
    for(let i=0;i<3;i++)g.add(cyl(.5*s,.5*s,20*s,darkSteel,(-5+i*2.5)*s,10*s,4*s));
    return g;},
  mine(s){const g=new THREE.Group(); // 矿井头架
    const fr=new THREE.Mesh(new THREE.ConeGeometry(6*s,16*s,4),darkSteel);
    fr.position.y=8*s;fr.castShadow=true;g.add(fr);
    g.add(box(2*s,18*s,2*s,steel,0,9*s,0));
    g.add(box(14*s,1.5*s,14*s,baseSkirt,0,.7*s,0));
    g.add(cyl(1.2*s,1.2*s,6*s,glowMat(0xffc060),0,19*s,0));
    return g;},
  warehouse(s){const g=new THREE.Group(); // 仓储平顶 + 集装箱
    g.add(box(18*s,7*s,12*s,hullC,0,3.5*s,0));
    for(let i=0;i<4;i++)g.add(box(3.5*s,2.5*s,3*s,[hullA,hullB,darkSteel][i%3],(-7+i*4.5)*s,8.5*s,(rnd()-.5)*6*s));
    return g;},
  garrison(s){const g=new THREE.Group(); // 安保哨塔
    g.add(cyl(3*s,4*s,14*s,darkSteel,0,7*s,0));
    g.add(box(7*s,3*s,7*s,winMat(false,1.2),0,15.5*s,0));
    g.add(cyl(.3*s,.3*s,6*s,darkSteel,0,20*s,0));
    g.add(sph(.7*s,glowMat(0xff4040),0,23*s,0));
    return g;},
  hub(s){const g=new THREE.Group(); // 指挥塔
    g.add(cyl(5*s,7*s,20*s,hullA,0,10*s,0));
    g.add(cyl(7*s,5*s,5*s,winMat(true,1.6),0,22*s,0));
    g.add(cyl(.5*s,.5*s,10*s,darkSteel,0,30*s,0));
    g.add(sph(1*s,glowMat(0x59d6ff),0,35*s,0));
    return g;},
  corewell(s){const g=new THREE.Group();   // Mk.II 陨核深井：环形井缘 + 稳定塔 + 能量柱
    g.add(cyl(11*s,12*s,3*s,hullA,0,1.5*s,0));
    for(let i=0;i<6;i++){const a=i/6*Math.PI*2;
      g.add(box(2.2*s,9*s,2.2*s,darkSteel,Math.cos(a)*7.5*s,7.5*s,Math.sin(a)*7.5*s));}
    g.add(cyl(1.2*s,1.2*s,16*s,glowMat(0xff8a35),0,8*s,0));
    return g;},
  pressure(s){const g=new THREE.Group();   // Mk.II 压力分离站：双肺 + 中央脊柱
    for(const sx of [-1,1])g.add(cyl(2.4*s,2.4*s,6*s,hullA,sx*3.2*s,3*s,0));
    g.add(box(2*s,9*s,2.6*s,darkSteel,0,4.5*s,0));
    g.add(sph(.8*s,glowMat(0x59d6ff),0,9.5*s,0));
    return g;},
  battery(s){const g=new THREE.Group();     // 储能站：并排电池柜 + 母线
    for(let i=0;i<3;i++)g.add(box(2.4*s,3.4*s,4*s,hullA,(i-1)*2.8*s,1.7*s,0));
    g.add(box(9*s,.5*s,.5*s,darkSteel,0,4*s,0));
    g.add(sph(.5*s,glowMat(0x8affc0),0,4.6*s,0));
    return g;},
  solar(s){const g=new THREE.Group();       // 太阳能阵列：倾斜板阵
    for(let i=0;i<3;i++){const p=box(4.2*s,.3*s,3*s,winMat(false,.9),(i-1)*4.6*s,2.6*s,0);
      p.rotation.x=-.5;g.add(p);g.add(cyl(.25*s,.25*s,2.6*s,darkSteel,(i-1)*4.6*s,1.3*s,0));}
    return g;},
  medical(s){const g=new THREE.Group();     // 医疗站：低矮舱 + 十字灯
    g.add(box(7*s,3.2*s,5*s,hullA,0,1.6*s,0));
    g.add(box(4*s,1.6*s,3*s,hullB,0,4*s,0));
    g.add(box(1.6*s,.3*s,.3*s,glowMat(0xff8fa8),0,5*s,0));
    g.add(box(.3*s,.3*s,1.6*s,glowMat(0xff8fa8),0,5*s,0));
    return g;},
  oxygentank(s){const g=new THREE.Group();  // 氧气储罐：卧式罐列
    for(let i=0;i<3;i++){const t=cyl(1.5*s,1.5*s,7*s,hullA,0,2*s,(i-1)*3.4*s);
      t.rotation.z=Math.PI/2;g.add(t);}
    g.add(box(9*s,.6*s,.6*s,darkSteel,0,.4*s,0));
    return g;},
  algae(s){const g=new THREE.Group();       // 藻类池：浅池 + 绿光
    g.add(box(9*s,1.2*s,6*s,darkSteel,0,.6*s,0));
    g.add(box(8*s,.3*s,5*s,glowMat(0x54ff9a),0,1.3*s,0));
    return g;},
  bunker(s){const g=new THREE.Group();      // Mk.II 辐射掩体：低矮长条 + 采光井
    g.add(box(16*s,3*s,9*s,hullA,0,1.5*s,0));
    g.add(box(10*s,2.4*s,6.5*s,hullA,-2*s,4.2*s,0));
    for(let i=0;i<4;i++)g.add(cyl(.6*s,.6*s,1.2*s,glowMat(0xffb060),(-5+i*3.4)*s,6*s,0));
    return g;},
};
const TYPE_KEYS=Object.keys(TYPES);
const TYPE_COLOR={habitat:0xffd9a0,industrial:0xff6a40,oxygen:0x59d6ff,power:0xffb060,water:0x40c8ff,
  food:0x54ff9a,science:0x6ab8ff,refinery:0xffa050,mine:0xffc060,warehouse:0xc0b49a,garrison:0xff5050,hub:0x59d6ff,
  corewell:0xff8a35,pressure:0x59d6ff,bunker:0xffd9a0,   // Mk.II Visual Anchor 系列
  battery:0x8affc0,solar:0xffe070,medical:0xff8fa8,oxygentank:0x59d6ff,algae:0x54ff9a};
const TYPE_SCALE={habitat:.6,industrial:.62,oxygen:.6,power:.6,water:.6,food:.6,science:.58,
  refinery:.6,mine:.6,warehouse:.62,garrison:.58,hub:.85,
  corewell:1.0,pressure:.6,bunker:.6,battery:.6,solar:.6,medical:.6,oxygentank:.6,algae:.6};

/* 中文建筑名 → 内部 type 键（用于建造时生成对应模型） */
const NAME2TYPE={"氧气站":"oxygen","氧气储罐":"oxygentank","水净化站":"water","陨核反应井":"core",
  "陨核深井 Mk.II":"corewell","压力分离站 Mk.II":"pressure","辐射掩体 Mk.II":"bunker",
  "太阳能阵列":"solar","储能站":"battery","热气发电机":"power","温室农场":"food","藻类池":"algae",
  "医疗站":"medical","宿舍":"habitat","仓库":"warehouse","车库":"hub","精炼厂":"refinery","安保哨塔":"garrison","采矿站":"mine","矿场":"mine","制造车间":"industrial"};

/* GLB 模板缓存：真正由 Blender 导出的模型，建造时克隆而非程序化生成 */
const GLB_CACHE={};   // type -> loaded scene template
const GLB_SRC={corewell:'models/building.corewell.mk2.glb',
  battery:'models/building.battery.mk2.glb',
  solar:'models/building.solar.mk2.glb',
  garrison:'models/building.garrison.mk2.glb',
  hub:'models/building.hub.mk2.glb',
  refinery:'models/building.refinery.mk2.glb',
  medical:'models/building.medical.mk2.glb',
  oxygentank:'models/building.oxygentank.mk2.glb',
  algae:'models/building.algae.mk2.glb',
  pressure:'models/building.pressure.mk2.glb',
  bunker:'models/building.bunker.mk2.glb',
  oxygen:'models/building.oxygen.mk1.glb',
  water:'models/building.water.mk1.glb',
  mine:'models/building.mine.mk1.glb',
  power:'models/building.power.mk1.glb',
  science:'models/building.lab.mk1.glb',
  warehouse:'models/building.warehouse.mk1.glb',
  food:'models/building.farm.mk1.glb',
  habitat:'models/building.habitat.mk1.glb',
  industrial:'models/building.industrial.mk1.glb',
  pylon:'models/infra.pylon.mk1.glb',
  pipe:'models/infra.pipeseg.mk1.glb',
  conveyor:'models/infra.conveyor.mk1.glb'};
const MK2_TYPES=new Set(['corewell','pressure','bunker','battery','solar','garrison',
                         'hub','refinery','medical','oxygentank','algae']);
const BUILD_UPSCALE=1.6;   // 建筑整体放大；坑体尺寸同比例放大以免重叠
// oxygen 重做后占地由 11.83m 变 13.13m，按 2.2 入场会比旧版宽 11% 并侵占邻格，
// 故等比降到 2.0 —— 世界尺寸与旧版持平。
// mine 重做成竖井井架后占地 12.23m -> 13.78m，同样等比降 scale 保持世界尺寸不变。
// industrial 是新补的模型（占地 13.40m）；1.5 让它的世界占地约 32，落在
// 同批 Mk.I 建筑 27~42 的中位，不会在工业区里显得突兀。
const GLB_BUILD_SCALE={oxygen:2.0,water:2.0,mine:1.40,power:1.6,science:1.1,warehouse:1.0,food:1.2,habitat:1.0,
  industrial:1.5,
  // Mk.II 锚点按真实米制建模，基准与上面这批不同：
  //   corewell 121m 是地标，坑底半径 384 -> 0.55 让它占据中心而不吞掉整个坑
  //   pressure 15m / bunker 33m 对齐旁边旧建筑的观感体量
  corewell:0.55, pressure:0.85, bunker:0.42,
  // 本批同为真实米制（garrison 19m 高塔 / hub 46m 地标 / 其余 5~17m）
  battery:1.15, solar:1.05, garrison:0.95, hub:0.42, refinery:1.05,
  medical:0.95, oxygentank:1.05, algae:1.25};   // GLB 真尺寸 → demo 比例
let _glbLoader=null;
/* 加载中去重：缓存检查只在加载【完成后】生效，而放置逻辑会在首次加载返回前
   为同类型的每一栋建筑各调一次 ensureGLB —— 于是 12 座矿井触发 12 次并行加载，
   得到 12 份互不共享的几何与贴图（实测 945 个 GLB 网格对应 939 份独立几何）。
   用 pending 队列保证每种类型只加载一次，其余等待同一结果。 */
const GLB_PENDING={};   // type -> callback[]
function ensureGLB(type,cb){
  if(GLB_CACHE[type]){cb(GLB_CACHE[type]);return;}
  const src=GLB_SRC[type]; if(!src)return;
  if(GLB_PENDING[type]){GLB_PENDING[type].push(cb);return;}
  GLB_PENDING[type]=[cb];
  _glbLoader=_glbLoader||new GLTFLoader();
  _glbLoader.load(src,gltf=>{
    const s=gltf.scene; s.traverse(o=>{if(o.isMesh){o.castShadow=true;o.receiveShadow=true;}});
    GLB_CACHE[type]=s;
    const q=GLB_PENDING[type]||[]; delete GLB_PENDING[type];
    q.forEach(f=>f(s));
  },undefined,()=>{delete GLB_PENDING[type];/* 失败则用程序化兜底 */});
}

initBuildingPool({ city, shadowDirty, typeColor: TYPE_COLOR });

/* 建筑实例化渲染池已抽到 ./building-pool。它持有 POOLS 与实例槽位 ——
   正是引发过 TDZ 的那类模块级集合，搬走后消费者必须 import。
   city / shadowDirty / TYPE_COLOR 用注入而非 import，保持依赖单向。 */
// 预载 GLB 并立即建池，建造时零等待
['oxygen','water','mine','power','science','warehouse','food','habitat','corewell','pressure','bunker',
 'battery','solar','garrison','hub','refinery','medical','oxygentank','algae','industrial']
  .forEach(t=>ensureGLB(t,tpl=>buildPool(t,tpl)));
['pylon','pipe','conveyor'].forEach(t=>ensureGLB(t,tpl=>buildPool(t,tpl,false)));


/* 统一朝向：模型正面是 Blender 的 -Y，导出 glTF 后为 +Z。
   rotation.y=θ 时正面指向 (sinθ,0,cosθ)；要指向陨核(原点)需 θ=-a-π/2。
   旧公式 -a+π/2 恰好差 180°—— 整城建筑都背对着核心。 */
function faceCore(x,z){ return -Math.atan2(z,x)-Math.PI/2; }
function building(x,z,s=1,type,rot?:number){
  type=type||TYPE_KEYS[Math.floor(rnd()*TYPE_KEYS.length)];
  // 朝向在创建时定死（默认正面朝核心）。旧 API 是先建再改 rotation.y ——
  // 实例矩阵在创建时已写入池，事后改锚点不会传导，故改为参数传入。
  const yaw=(rot!==undefined)?rot:faceCore(x,z);
  if(GLB_SRC[type]){
    const mk=(bId:number,cellKey?:string,entityId?:string)=>{
      const anchor=new THREE.Group();
      anchor.scale.setScalar((GLB_BUILD_SCALE[type]||1)*BUILD_UPSCALE);
      anchor.position.set(x,craterHeight(x,z),z);
      anchor.rotation.y=yaw;
      anchor.userData.bType=type; anchor.userData.bId=bId;
      if(cellKey)anchor.userData.cellKey=cellKey;
      // entityId 必须一起继承：占位体转正时漏掉它，这栋楼就永远与内核实体断链，
      // 详情面板会退回「地标 · 非建造实体」，拆除也找不到该拆谁。
      if(entityId)anchor.userData.entityId=entityId;
      anchor.userData.pickRoot=anchor;      // 兼容既有的 city.traverse 检索
      city.add(anchor);
      poolAlloc(type,anchor);
      attachTier(anchor, MK2_TYPES.has(type)?1:0, TYPE_COLOR[type]||0x9fd8ff);
      return anchor;
    };
    if(POOLS[type]){ bid++; return mk(bid); }
    // 模板未就绪：程序化占位，加载完成后原地转正（继承 bId/cellKey）
    const standin=_proceduralBuilding(x,z,s,type,yaw);
    ensureGLB(type,tpl=>{
      if(!standin.parent)return;            // 已被拆除
      buildPool(type,tpl);
      mk(standin.userData.bId,standin.userData.cellKey,standin.userData.entityId);
      // 占位体不再保留：隐藏件不画但仍占几何内存，还会被每次 hover 的
      // 光线检测遍历（raycaster 不看 visible）。转正即销毁。
      city.remove(standin);
      standin.traverse((o:AnyObj)=>{if(o.isMesh)o.geometry.dispose();});   // 材质是共享缓存，不动
    });
    return standin;
  }
  return _proceduralBuilding(x,z,s,type,yaw);
}
function _proceduralBuilding(x,z,s,type,rot?:number){
  s*=TYPE_SCALE[type]||.6;
  // 新建筑类型只加进 GLB_SRC/TYPE_COLOR 而忘了给程序化实现，会在这里整页崩掉
  // （本项目已犯过两次）。退化到 industrial 至少让场景继续跑，并在控制台点名。
  if(typeof TYPES[type]!=='function'){
    console.error('[CZ] 建筑类型缺少程序化实现，已回退 industrial:',type);
    type='industrial';
  }
  const g=TYPES[type](s);
  // type marker beacon so俯视图可读
  const beacon=new THREE.Mesh(new THREE.SphereGeometry(1.1*s,8,8),
    new THREE.MeshBasicMaterial({color:TYPE_COLOR[type]||0xffffff}));
  beacon.position.y=30*s; g.add(beacon);
  g.position.set(x,craterHeight(x,z),z);
  g.rotation.y=(rot!==undefined)?rot:faceCore(x,z);
  g.traverse(o=>{if(o.isMesh)o.castShadow=true;});
  g.userData.bType=type; g.userData.bId=bid+1;
  g.traverse(o=>{o.userData.pickRoot=g;});
  city.add(g); bid++;
  shadowDirty();
  return g;
}

/* ================= 建造排布：环形分区网格 =================
   原来建筑是纯随机撒点、无碰撞检测，所以既会互相穿插，也读不出任何城市规划。
   改为「环 × 扇区」单元格：以陨核井为中心，同心环划分距离带，放射干线划分扇区，
   每个单元格最多一栋建筑。分区规则来自功能依赖，而不是随机：
     生命支持/居住 靠近核心（供能与保温半径内）
     生产/工业     中间带（靠近能源但远离居住区）
     采矿/防务     外环（贴近矿脉与坑壁）
   玩家放置时预览会吸附到最近的空闲单元格，占用/越界会变红。 */
/* 网格常量与换算全部来自内核（kernel-bridge 的 MAP_CONFIG）：
   两套坐标各算各的正是接线前的病根，这里不再保留本地副本。 */
const RING_R0 = K.RING_R0;
const RING_STEP = K.RING_STEP;
const RING_COUNT = K.RING_COUNT;
/* 扇区数必须【随半径变化】。固定 24 扇区时内环切向间距只有
   2π·96/24 ≈ 25 世界单位，而建筑本体是 27~42 单位 —— 内圈必然穿模，
   外圈又空得离谱（第 6 环 97 单位放一栋 34 单位的房子）。
   改为按目标间距 CELL_PITCH 反算，并取 6 的倍数以保持与放射干线对齐。 */
const CELL_PITCH = K.CELL_PITCH;   // 单元格目标切向弧长（略大于最宽建筑 42）
const SEC_OF = K.secOf;
const wrapSct = K.wrapSct;
const cellKey = K.cellKey;
const cellCenter = K.cellCenter;
const worldToCell = K.worldToCell;
/* 占用状态读内核 spatial —— 本地不再持有 cellTaken 集合 */
const cellIsTaken = (r:number, sct:number) => K.isCellTaken(r, sct);
/* 各类型允许的环区间（按功能依赖，不是随便分的） */
const ZONE = {
  habitat:[0,3], bunker:[0,3], oxygen:[0,2], oxygentank:[0,2], water:[0,3],
  food:[0,3], algae:[0,3], medical:[0,3], pressure:[0,3],
  power:[1,5], battery:[1,5], solar:[1,5], industrial:[2,5], refinery:[2,6],
  warehouse:[1,5], science:[1,4], hub:[0,2], corewell:[2,5],
  parts_workshop:[2,5], recycler:[2,6],   // 加工区：与制造车间/精炼厂同带
  mine:[4,6], garrison:[4,6],
};
function cellAllowed(type, r){
  if (r < 0 || r >= RING_COUNT) return false;
  const z = ZONE[type]; if (!z) return true;
  return r >= z[0] && r <= z[1];
}
/* 从期望单元格向外螺旋搜索最近的合法空位 */
function findFreeCell(type, r0, a0){
  // 各环扇区数不同，螺旋搜索必须以【角度】为基准换算到目标环的扇区序号
  for (let d = 0; d < RING_COUNT * 60; d++){
    const r = r0 + ((d % 5) - 2);
    if (!cellAllowed(type, r)) continue;
    const n = SEC_OF(r);
    const sct = Math.floor(a0 / (Math.PI * 2) * n) + Math.floor(d / 5) * (d % 2 ? 1 : -1);
    if (!cellIsTaken(r, sct)) return { r, sct: wrapSct(r, sct), key: cellKey(r, sct) };
  }
  return null;
}

/* ---------- 电缆：与道路同一套贴地弧带；账本同样在内核 ----------
   y 偏移比道路高 0.6，避免与路面共面 z-fighting。
   电缆不占建筑格 —— 建筑必须能接到电，这是与道路的唯一规则差异。 */
const cableOkMat=new THREE.MeshBasicMaterial({color:0x53e08a,transparent:true,opacity:.5,
  side:THREE.DoubleSide,depthWrite:false,blending:THREE.AdditiveBlending});
const cableBadMat=new THREE.MeshBasicMaterial({color:0xff5d5d,transparent:true,opacity:.5,
  side:THREE.DoubleSide,depthWrite:false,blending:THREE.AdditiveBlending});
function cableSegment(r:number,sct:number,mat:AnyObj){
  const g=new THREE.Group();
  g.add(roadRibbon(r,sct,3.2,1.6,mat));
  return g;
}
const cablePreview=new THREE.Group(); cablePreview.visible=false; scene.add(cablePreview);

/* ---------- 管道：与电缆同构，账本同样在内核 ----------
   氧气与水共用一张管网（Non-goal 排除了真实流体模拟），因此只有一种管道。
   y 偏移 2.2，压在电缆之上；关闭的隔离阀用暗红标出。 */
const pipeOkMat=new THREE.MeshBasicMaterial({color:0x53e08a,transparent:true,opacity:.5,
  side:THREE.DoubleSide,depthWrite:false,blending:THREE.AdditiveBlending});
const pipeBadMat=new THREE.MeshBasicMaterial({color:0xff5d5d,transparent:true,opacity:.5,
  side:THREE.DoubleSide,depthWrite:false,blending:THREE.AdditiveBlending});
function pipeSegment(r:number,sct:number,mat:AnyObj){
  const g=new THREE.Group();
  g.add(roadRibbon(r,sct,2.4,2.2,mat));
  return g;
}
const pipePreview=new THREE.Group(); pipePreview.visible=false; scene.add(pipePreview);
let pipeCell:{r:number;sct:number;key:string}|null=null, pipeOk=false, pipeWhy='';
function updatePipePreview(x:number,z:number){
  const {r,sct}=worldToCell(x,z);
  const laid=K.hasPipe(r,sct);
  pipeOk=!laid; pipeWhy=laid?'此处已有管道':'';
  pipeCell={r,sct,key:cellKey(r,sct)};
  pipePreview.clear();
  pipePreview.add(pipeSegment(r,sct,pipeOk?pipeOkMat:pipeBadMat));
}
const PIPE_REJECT:Record<string,string>={
  PIPE_CELL_INVALID:'此处不是有效单元格',
  PIPE_ALREADY_EXISTS:'此处已有管道',
  PIPE_NOT_FOUND:'此处没有管道',
  PIPE_RESOURCES_INSUFFICIENT:'金属不足，无法铺设管道',
  VALVE_NO_PIPE:'此处没有管道，无法安装阀门',
  VALVE_ALREADY_IN_STATE:'阀门已是该状态',
};
/** 把内核里的一格管道同步成场景网格（预置与玩家铺设共用）。 */
/* 三个 add*Mesh 现在只标脏 —— 合并层由 rebuildInfraLayers 统一整体重建。
   保留函数名是为了不改动十几处调用点；播种 210 格时会标脏 210 次，
   但真正的重建每帧至多一次（见主循环）。 */
function addPipeMesh(_r:number,_sct:number){ markInfraDirty(); }
function placePipe(){
  if(!pipeCell)return;
  if(!pipeOk){toast('⚠️ '+(pipeWhy||'此处无法铺设'));return;}
  const res=K.layPipe(pipeCell.r,pipeCell.sct);
  if(!res.ok){toast('⛔ '+(PIPE_REJECT[res.code||'']||res.code||'无法铺设'));return;}
  addPipeMesh(pipeCell.r,pipeCell.sct);
  /* 段件用既有的 GLB 实例池渲染（视觉不退化），但账本在内核 ——
     管道因此不再依赖本地 infraTaken 集合，那是 G008A 起就欠着的技术债。 */
  for(const sp of infraSpots('pipe',pipeCell.r,pipeCell.sct)){
    const a=new THREE.Group();
    a.scale.setScalar(BUILD_UPSCALE);
    a.position.set(sp.x,craterHeight(sp.x,sp.z),sp.z);
    a.rotation.y=sp.rot;
    a.userData.bType='pipe';
    city.add(a);
    if(POOLS['pipe'])poolAlloc('pipe',a);
  }
  onStockChange(); onFluidChange();
  markDirty(); toast(`💧 管道已铺设（第 ${pipeCell.r+1} 环）`);
  addLog(`管道铺设完成 · 第 ${pipeCell.r+1} 环 · 累计 ${countInfra(K.hasPipe)} 段`);
  pipeOk=false; pipeWhy='此处已有管道';
  pipePreview.clear();
  pipePreview.add(pipeSegment(pipeCell.r,pipeCell.sct,pipeBadMat));
}
/** 阀门工具：点已有管道即切换开/关。 */
function toggleValve(){
  if(!pipeCell)return;
  const r=pipeCell.r,sct=pipeCell.sct;
  if(!K.hasPipe(r,sct)){toast('⚠️ 此处没有管道');return;}
  const next=!K.valveClosed(r,sct);
  const res=K.setValve(r,sct,next);
  if(!res.ok){toast('⛔ '+(PIPE_REJECT[res.code||'']||res.code||'无法操作'));return;}
  addPipeMesh(r,sct);
  onFluidChange();
  markDirty();
  toast(next?`🔒 隔离阀已关闭（第 ${r+1} 环）`:`🔓 隔离阀已打开（第 ${r+1} 环）`);
  addLog(next?`隔离阀关闭 · 第 ${r+1} 环 · 管网已分段`:`隔离阀打开 · 第 ${r+1} 环`);
}
let cableCell:{r:number;sct:number;key:string}|null=null, cableOk=false, cableWhy='';
function updateCablePreview(x:number,z:number){
  const {r,sct}=worldToCell(x,z);
  const laid=K.hasCable(r,sct);
  cableOk=!laid;
  cableWhy=laid?'此处已有电缆':'';
  cableCell={r,sct,key:cellKey(r,sct)};
  cablePreview.clear();
  cablePreview.add(cableSegment(r,sct,cableOk?cableOkMat:cableBadMat));
}
const CABLE_REJECT:Record<string,string>={
  CABLE_CELL_INVALID:'此处不是有效单元格',
  CABLE_ALREADY_EXISTS:'此处已有电缆',
  CABLE_NOT_FOUND:'此处没有电缆',
  CABLE_RESOURCES_INSUFFICIENT:'金属不足，无法铺设电缆',
};
/** 把内核里的一格电缆同步成场景网格（预置与玩家铺设共用同一条路径）。 */
function addCableMesh(_r:number,_sct:number){ markInfraDirty(); }
function placeCable(){
  if(!cableCell)return;
  if(!cableOk){toast('⚠️ '+(cableWhy||'此处无法铺设'));return;}
  const res=K.layCable(cableCell.r,cableCell.sct);
  if(!res.ok){toast('⛔ '+(CABLE_REJECT[res.code||'']||res.code||'无法铺设'));return;}
  addCableMesh(cableCell.r,cableCell.sct);
  onStockChange(); onPowerChange();
  markDirty(); toast(`⚡ 电缆已铺设（第 ${cableCell.r+1} 环）`);
  addLog(`电缆铺设完成 · 第 ${cableCell.r+1} 环 · 累计 ${countInfra(K.hasCable)} 段`);
  cableOk=false; cableWhy='此处已有电缆';
  cablePreview.clear();
  cablePreview.add(cableSegment(cableCell.r,cableCell.sct,cableBadMat));
}

/* floor districts: thematic clusters around a clear core */
/* 演示城的建筑清单。
   G011 曾把零件工坊与回收场加进这个池子，好让生产链在游戏里可见 —— 但这是个
   随机权重池，两种新楼各被播种约 10 座，电力需求随之 +10560W，演示城由此
   **电力赤字**（供 60540 < 需 71100），触发限电级联。
   为了让新功能可见而改坏既有平衡，代价大于收益，故撤回：两栋楼只进建造菜单，
   由玩家自行建造。演示城的平衡不属于 G011 范围（Non-goal：不做最终平衡）。 */
const floorTypes=['habitat','habitat','industrial','oxygen','water','food','warehouse','power','bunker','pressure','medical','algae','oxygentank'];
/* 按环形网格铺城：每个单元格至多一栋，类型受 ZONE 分区约束 */
function placeOnGrid(type, ringHint, scale){
  const a0 = rnd() * Math.PI * 2;
  const cell = findFreeCell(type, ringHint, a0);
  if (!cell) return null;
  // 既有殖民地不是玩家建的：走 seed_building（直接 operational、不扣当期库存），
  // 但依旧是命令 —— 占用由内核落账，渲染层不再自己记账
  const seeded = K.seedBuilding(type, cell.r, cell.sct, 0);
  if (!seeded.ok) return null;
  const c = cellCenter(cell.r, cell.sct);
  const jx = (rnd() - .5) * 10, jz = (rnd() - .5) * 10;   // 轻微抖动，避免过于机械
  const b = building(c.x + jx, c.z + jz, scale, type);
  if (b){
    b.rotation.y = faceCore(c.x, c.z);                     // 正面朝核心
    b.userData.cellKey = cell.key;
    b.userData.entityId = seeded.entityId;                 // 拆除/详情按实体 ID 找内核
  }
  return b;
}
function seedDemoCity(){
/* 示范殖民地是一座已运转 127 天的殖民地 —— 时钟随场景设定一起给出。
   「从零开始」则保持第 1 天（产品宪章：玩家从坠毁后第 1 天开始）。 */
K.setClock(20*60+21,127);
for(let ring=0;ring<4;ring++){
  const n=10+ring*5;
  for(let i=0;i<n;i++){
    placeOnGrid(floorTypes[Math.floor(rnd()*floorTypes.length)], ring, 1+rnd()*.4);
  }
}
/* lower wall terraced band */
for(let i=0;i<26;i++) placeOnGrid(i%4===0?'bunker':'habitat', 3, .8+rnd()*.3);
/* upper wall / rim terraces — industry & power on the walls */
const wallTypes=['industrial','power','refinery','mine','habitat','warehouse','pressure','solar','battery'];
for(let i=0;i<44;i++) placeOnGrid(wallTypes[Math.floor(rnd()*wallTypes.length)], 4+Math.floor(rnd()*2), .85+rnd()*.4);
/* outer rim: mines, garrisons, science */
const rimTypes=['mine','garrison','science','warehouse','industrial','refinery'];
for(let i=0;i<20;i++) placeOnGrid(rimTypes[Math.floor(rnd()*rimTypes.length)], 6, .9+rnd()*.4);
/* landmark command hub on the floor */
building(60,40,1.4,'hub');
/* Mk.II Visual Anchor 地标：陨核深井放在坑底另一侧，与中央旧核并存对照 */
building(-150,150,1.0,'corewell');
/* 既有殖民地自带电网：沿 0~6 环整环铺缆。
   走 seed_cable（不扣当期库存）—— 与 seed_building 同理，这些不是玩家铺的。
   整环连通保证所有预置建筑都接得上电，同时给玩家一个可见的电网参照。 */
for(let r=0;r<RING_COUNT;r++){
  const n=SEC_OF(r);
  for(let sct=0;sct<n;sct++)K.seedCable(r,sct);
}
K.flush();
for(let r=0;r<RING_COUNT;r++){
  const n=SEC_OF(r);
  for(let sct=0;sct<n;sct++)if(K.hasCable(r,sct))addCableMesh(r,sct);
}
/* 管网同理预置。氧气与水共用一张网，因此与电网同位铺满即可 ——
   走 seed_pipe（不扣当期库存），既有殖民地的管网不是玩家铺的。 */
for(let r=0;r<RING_COUNT;r++){
  const n=SEC_OF(r);
  for(let sct=0;sct<n;sct++)K.seedPipe(r,sct);
}
K.flush();
for(let r=0;r<RING_COUNT;r++){
  const n=SEC_OF(r);
  for(let sct=0;sct<n;sct++)if(K.hasPipe(r,sct))addPipeMesh(r,sct);
}
}

/* ================= 会话生命周期（G014） =================
   开局不再是「模块加载即开始」，而是由 shell.ts 的状态机驱动：
   主菜单 → 新游戏/继续/读档 → 游戏中 → Esc 暂停 → 返回主菜单/退出。
   本段只提供渲染层的清场与重建，不碰任何领域状态。 */

/** 清空场景里的全部殖民地内容。漏一处就会在下一局残留，因此逐类列举。 */
function teardownScene(){
  /* 0) 相机。它不是场景对象，但同样是【跨局残留】的状态：camera-rig 是模块级
     单例，清场不碰它，于是新一局会沿用上一局的焦点。若上一局把镜头平移到了
     坑外，新一局开局就是漆黑一片（夜间的坑外空地）。
     读档路径随后会用 restoreView 覆盖，所以放在最前面是安全的。 */
  resetCamNow();
  // 1) 建筑与基建的锚点（含 GLB 实例与程序化占位体）
  const doomed:AnyObj[]=[];
  city.traverse((o:AnyObj)=>{if(o.userData&&o.userData.pickRoot===o)doomed.push(o);});
  for(const root of doomed){
    if(root.userData.slot!=null)poolFree(root);
    city.remove(root);
    root.traverse((o:AnyObj)=>{
      if(o.isSprite){const i=tierSprites.indexOf(o);if(i>=0)tierSprites.splice(i,1);
        o.material.dispose();}
    });
  }
  // 2) 实例池：计数归零即可，模板几何与材质留给下一局复用（重建它们很贵）
  for(const type in POOLS){
    const pool=POOLS[type];
    pool.records.length=0;
    for(const {im} of pool.meshes)im.count=0;
  }
  // 3) 道路 / 电缆 / 管道的程序化网格 —— 不在池里，必须逐个 dispose
  disposeInfrastructure();
  infraTaken.clear();
  // 4) 选中与悬停状态
  clearSel(); hoverBldg=null; binfoEl.classList.remove('show');
  bid=0;
  shadowDirty();
}

/** 按当前内核状态重建场景。seedDemo=true 时额外播种示范殖民地。 */
function rebuildScene(opts:{seedDemo:boolean}){
  if(opts.seedDemo){
    seedDemoCity();
    /* 岗位分配必须在建筑就位【之后】跑。
       newSession() 里也调了一次，但那时城里一栋楼都没有 —— 分配器找不到岗位，
       结果是 48 名居民全部失业（浏览器实测 employed:0 就是这么来的）。
       读档路径不需要：存档里已带着指派。 */
    K.rebalanceJobs();
    onPopulationChange();
    return;
  }
  /* 读档路径：内核里已有实体与管网，照着它把渲染层建回来。
     顺序无所谓 —— 每栋建筑的位置由它自己的锚点格决定，不依赖遍历次序。 */
  for(const {entityId,building:bc} of K.allBuildings()){
    const a=bc.anchor;
    const c=cellCenter(a.ring,a.sector);
    const b=building(c.x,c.z,1.15,bc.defId,faceCore(c.x,c.z));
    if(b){b.userData.cellKey=cellKey(a.ring,a.sector);b.userData.entityId=entityId;}
  }
  for(let r=0;r<RING_COUNT;r++){
    const n=SEC_OF(r);
    for(let sct=0;sct<n;sct++){
      if(K.hasCable(r,sct))addCableMesh(r,sct);
      if(K.hasPipe(r,sct))addPipeMesh(r,sct);
    }
  }
  onStockChange(); onPowerChange(); onFluidChange(); onPopulationChange();
  shadowDirty();
}

/* initShell 的调用挪到模块末尾 —— 它是入口，会立刻回调 teardown/rebuild，
   而那些函数依赖 roadObjs / shellPaused 等模块级 const/let。写在这里会 TDZ。
   本文件已因同一原因崩过四次（COST_BY_TYPE / cableObjs / seq / shellPaused），
   根因是 2500 行单模块把「声明顺序」和「执行顺序」耦死了。 */

/* （此处原为氧气站 GLB 升级 + InstancedMesh 概念验证，已被通用建筑渲染池取代） */

/* ================= WALL ELEVATORS + ZIGZAG RAMPS ================= */
for(let i=0;i<8;i++){
  const a=i/8*Math.PI*2+.4, rr=FLOOR_R+(RIM_R-FLOOR_R)*.6;
  const x=Math.cos(a)*rr,z=Math.sin(a)*rr,baseY=craterHeight(x,z);
  const th=55+rnd()*35;
  const t=new THREE.Mesh(new THREE.CylinderGeometry(2.2,3,th,8),steel);
  t.position.set(x,baseY+th/2,z); t.castShadow=true; city.add(t);
  const lamp=new THREE.Mesh(new THREE.SphereGeometry(1.5,8,8),new THREE.MeshBasicMaterial({color:0xffc060}));
  lamp.position.set(x,baseY+th+1,z); city.add(lamp);
  // radial support cable toward core
  const inner=new THREE.Vector3(Math.cos(a)*70,craterHeight(Math.cos(a)*70,Math.sin(a)*70)+6,Math.sin(a)*70);
  const top=new THREE.Vector3(x,baseY+th,z);
  city.add(new THREE.Mesh(new THREE.TubeGeometry(new THREE.LineCurve3(top,inner),1,.28,4),darkSteel));
}
/* switchback ramp segments climbing the wall */
for(let k=0;k<3;k++){
  const a0=k*2.1+.5;
  for(let s=0;s<6;s++){
    const t0=s/6,t1=(s+1)/6;
    const r0=lerp(FLOOR_R+8,RIM_R-10,t0), r1=lerp(FLOOR_R+8,RIM_R-10,t1);
    const aa=a0+(s%2?-1:1)*(.12+t0*.1);
    const x0=Math.cos(aa)*r0,z0=Math.sin(aa)*r0,x1=Math.cos(aa)*r1,z1=Math.sin(aa)*r1;
    const y0=craterHeight(x0,z0)+1,y1=craterHeight(x1,z1)+1;
    const seg=new THREE.Mesh(new THREE.BoxGeometry(Math.hypot(x1-x0,z1-z0),.7,2.4),
      new THREE.MeshStandardMaterial({color:0x6a5a48,metalness:.3,roughness:.7,emissive:0xff9040,emissiveIntensity:.15}));
    seg.position.set((x0+x1)/2,(y0+y1)/2,(z0+z1)/2);
    seg.rotation.y=Math.atan2(-(z1-z0),(x1-x0));
    seg.rotation.z=Math.atan2(y1-y0,Math.hypot(x1-x0,z1-z0));
    city.add(seg);
  }
}

/* ================= ENERGY GRID (sci-fi glowing conduits) ================= */
// 主环网：亮青能量管
const gridCyan=new THREE.MeshBasicMaterial({color:0x49e6ff,transparent:true,opacity:.85});
const gridCyanDim=new THREE.MeshBasicMaterial({color:0x2fb6dd,transparent:true,opacity:.5});
for(let ring=0;ring<3;ring++){
  const rr=176+ring*88;
  const torus=new THREE.Mesh(new THREE.TorusGeometry(rr,1.1,4,120),gridCyan);
  torus.rotation.x=Math.PI/2; torus.position.y=2.4; city.add(torus);
}
// 放射干线
for(let i=0;i<12;i++){
  const a=i/12*Math.PI*2,len=FLOOR_R*.78;
  const spoke=new THREE.Mesh(new THREE.BoxGeometry(len,.7,1.5),i%2?gridCyanDim:gridCyan);
  spoke.position.set(Math.cos(a)*len/2,2.6,Math.sin(a)*len/2); spoke.rotation.y=-a; city.add(spoke);
}
// 次级细网：更密的同心细环，营造电路板质感
for(let ring=0;ring<4;ring++){
  const rr=93+ring*61;
  const torus=new THREE.Mesh(new THREE.TorusGeometry(rr,.4,3,140),gridCyanDim);
  torus.rotation.x=Math.PI/2; torus.position.y=2.2; city.add(torus);
}
// 能量节点：环与放射线交点的发光点
const nodeGeo=new THREE.SphereGeometry(1.6,8,8);
const nodeMat=new THREE.MeshBasicMaterial({color:0x8ff2ff});
for(let ring=0;ring<3;ring++){const rr=176+ring*88;
  for(let i=0;i<12;i++){const a=i/12*Math.PI*2;
    const n=new THREE.Mesh(nodeGeo,nodeMat);
    n.position.set(Math.cos(a)*rr,3,Math.sin(a)*rr); city.add(n);}}

/* ================= DISTRICT LABELS ================= */
const districts=[["居住区",-170,90],["工业区",150,-120],["物流枢纽",180,80],["矿区 2 号",-140,-150],["陨核井",0,80],["地下层入口",40,210]];
const labelEls=districts.map(([txt,x,z])=>{
  const el=document.createElement('div');el.className='label';el.textContent=String(txt);
  document.getElementById('labels').appendChild(el);
  return {el,pos:new THREE.Vector3(x as number,craterHeight(x as number,z as number)+52,z as number)};
});
const _v=new THREE.Vector3();
function updateLabels(){
  labelEls.forEach(({el,pos})=>{
    _v.copy(pos).project(camera);
    const vis=_v.z<1;
    el.style.display=vis?'block':'none';
    if(vis){el.style.left=(_v.x*.5+.5)*innerWidth+'px';el.style.top=(-_v.y*.5+.5)*innerHeight+'px';}
  });
}

/* ================= UI ================= */
/* ================= 建筑目录 =================
   梳理自 GLB_SRC / BINFO / ZONE：全部 20 个有模型的建筑类型都在此登记。
   旧版只列了 13 项，且菜单名与 BINFO 名是两套（「水净化站」vs「钻水站」），
   靠 NAME2TYPE 反查名字才能拿到 type —— 改名就会静默错位。
   现在每条直接带 t（type 键），名字只是显示用，反查链路取消。 */
const LIB:Record<string,AnyObj[]>={
 "生命":[
  {t:'oxygen',    n:'制氧厂',            d:'从稀薄大气中分离并加压氧气，经管网输往各居住与工作单元。',            c:{metal:120,power:60}},
  {t:'oxygentank',n:'氧气储罐',          d:'高压储存氧气，平抑产耗波动，磁暴断电时维持供氧。',                  c:{metal:80}},
  {t:'pressure',  n:'压力分离站',        d:'双肺压力舱 + 外部压力平衡机构，处理坑内剧烈波动的气压。',           c:{metal:260,rare:20,cell:8}, mk:'II'},
  {t:'water',     n:'钻水站',            d:'深井钻探陨坑地下冰层并初步净化，供给饮用、农业与工业冷却。',        c:{metal:140,power:45}},
  {t:'medical',   n:'医疗站',            d:'处理创伤、辐射病与减压伤，降低人口非自然减员。',                    c:{metal:130}},
  {t:'habitat',   n:'居住穹顶',          d:'加压密封居住单元，提供温控、辐射屏蔽与基础生活空间。',              c:{metal:60}},
  {t:'bunker',    n:'辐射掩体',          d:'半地下覆土掩体，磁暴期间可容纳全部地表作业人员。',                  c:{metal:190,concrete:240}, mk:'II'},
 ],
 "能源":[
  {t:'core',      n:'陨核反应井',        d:'殖民地的能量心脏。全局唯一地标，已随殖民地建成，不可重复建造。',    c:{}, lock:'已建成'},
  {t:'corewell',  n:'陨核深井',          d:'下沉深井 + 三级束缚环 + 六座承力稳定塔，直接开采陨核能量。',        c:{metal:1200,rare:80,concrete:600,cell:40}, mk:'II'},
  {t:'solar',     n:'太阳能阵列',        d:'白天发电，产能随昼夜与磁暴强烈波动，需配合储能使用。',              c:{metal:200}},
  {t:'battery',   n:'储能站',            d:'大型线圈储能阵列，平抑电网峰谷并提供黑启动能力。',                  c:{metal:150,cell:20}, mk:'II'},
  {t:'power',     n:'热气发电机',        d:'消耗燃料的应急电源，磁暴或反应井停机时接管基础负载。',              c:{metal:180}},
 ],
 "生产":[
  {t:'food',      n:'温室农场',          d:'加压温室与人工光照，基础食物产能，耗电高但稳定。',                  c:{metal:100,water:30}},
  {t:'algae',     n:'藻类池',            d:'高密度藻类培养，单位面积产量远高于温室，但耗电更高。',              c:{metal:160,power:80}},
  {t:'mine',      n:'矿井',              d:'竖井井架 + 天轮提升，向坑壁岩层深处开采金属矿脉。',                c:{metal:170,concrete:120}},
  {t:'refinery',  n:'精炼厂',            d:'将粗矿冶炼为可用金属与合金，是一切建造的上游。',                    c:{metal:220,power:70}},
  {t:'industrial',n:'制造车间',          d:'熔炉 + 锻压机 + 密闭惰性气氛加工舱，就地制造结构件与备件。',        c:{metal:240,power:95}},
  {t:'parts_workshop',n:'零件工坊',      d:'把金属加工成备件。备件是全殖民地设施维护的唯一消耗品，断供即全线老化。', c:{metal:140,concrete:60}},
  {t:'recycler',  n:'回收场',            d:'补给无法从坑外运入，报废件必须回到循环。回收有损耗，环是净亏的。',   c:{metal:90,concrete:40}},
  {t:'science',   n:'科研站',            d:'磁场隔离环下的陨核物质研究设施，解锁新技术与建筑。',                c:{metal:180,rare:30}},
 ],
 "物流":[
  {t:'warehouse', n:'仓储中枢',          d:'龙门吊与立体货架，提升全局库存上限与物流吞吐。',                    c:{metal:110}},
  {t:'hub',       n:'指挥中枢',          d:'车辆库与调度中心，远征任务的出发地。',                              c:{metal:220}},
  {t:'garrison',  n:'安保哨塔',          d:'外环防务节点，维持秩序并应对坑壁塌方与外部威胁。',                  c:{metal:150,rare:10}},
 ],
};
const CATS=Object.keys(LIB);
const catIcons:Record<string,string>={"生命":"i-life","能源":"i-energy","生产":"i-prod","物流":"i-logi"};
/* 建造菜单里的名字统一并入 NAME2TYPE，供世界标签/详情面板反查 */
CATS.forEach(c=>LIB[c].forEach((b:AnyObj)=>{NAME2TYPE[b.n]=b.t;}));
/* BINFO 只写了 13 类，其余会把类型码当名字显示（如「solar 已拆除」）。
   LIB 里每一类都有中文名，反查即可兜底。 */
const TYPE2NAME:Record<string,string>={};
CATS.forEach(c=>LIB[c].forEach((b:AnyObj)=>{TYPE2NAME[b.t]=b.n;}));
const resNames={metal:"金属",rare:"稀有金属",concrete:"混凝土",cell:"能量单元",power:"电力",water:"水"};
/* ================= 建造资源闭环 =================
   库存类资源（metal/rare/concrete/cell）建造时扣减、拆除返还 60%；
   power/water 是每日负载，只展示不入库存账。
   金属与顶栏同一账本，扣减实时反映到 HUD。 */
/* 库存是内核的，这里只读投影。spend/refund 已随命令下沉 —— 渲染层无权改账。 */
const STOCK_KEYS=new Set(['metal','rare','concrete','cell']);
function shortages(cost:AnyObj):string[]{
  const stock=K.stock();
  return Object.entries(cost||{})
    .filter(([k,v])=>STOCK_KEYS.has(k)&&(stock[k]??0)<(v as number))
    .map(([k])=>k);
}
/* LIB 的 c 含 power/water 每日负载（仅展示），内核造价只含库存类资源。
   两者在库存键上必须一致，否则 UI 显示的价格与真实扣账会悄悄分叉。 */
CATS.forEach(c=>LIB[c].forEach((b:AnyObj)=>{
  const kernelCost=K.costOf(b.t);
  for(const [res,amt] of Object.entries(b.c||{})){
    if(!STOCK_KEYS.has(res))continue;
    if(kernelCost[res]!==amt)
      console.error(`[G008A] 造价分叉 ${b.t}.${res}: UI=${amt} 内核=${kernelCost[res]}`);
  }
}));
/* 顶栏各项的可寻址句柄。金属是事件驱动（放置/拆除后刷），电力是连续量，
   走 0.5s 模拟节拍刷 —— 两者节奏不同，不能混在一个回调里。 */
const _resEls:Record<string,{num:HTMLElement|null;unit:HTMLElement|null;sub:HTMLElement|null}>={};
let _metalEl:HTMLElement|null=null;   // 顶栏金属数值，从内核库存投影
function onStockChange(){
  if(_metalEl)_metalEl.textContent=String(K.stock().metal??0);
  onEconomyChange();
  refreshCosts();
}

/* ---------- G011 顶栏经济投影 ----------
   「食物」原先显示 `--` 并标注「待 G011」——现在它有真实来源了（菌床产出）。
   注解一律显示模拟层给出的**日净值**，不在 UI 侧做任何推算：
   净值的定义（产出 - 消耗，按日窗口结算）属于领域逻辑，UI 只做映射。 */
/**
 * 人口顶栏投影（G012）。
 *
 * 显示「总人口 / 此刻在岗」而不是「总人口 / 已就业」：占岗是全天的，
 * 在岗看班次 —— 玩家关心的是「现在有多少人在干活」。
 */
function onPopulationChange(){
  const p=K.population();
  const el=_resEls['人口']; if(!el)return;
  el.num.textContent=String(p.total);
  if(el.sub){
    el.sub.textContent=`在岗 ${p.onShiftNow}`;
    // 有人伤亡时转红：这是玩家必须立刻看到的信号
    const hurt=p.dead+p.incapacitated;
    el.sub.style.color=hurt>0?'var(--red)':'var(--muted)';
    if(hurt>0)el.sub.textContent=`在岗 ${p.onShiftNow} · 伤亡 ${hurt}`;
  }
}

function onEconomyChange(){
  const eco=K.economy();
  for(const row of eco.resources){
    const el=_resEls[RES_LABEL[row.id]??''];
    if(!el)continue;
    if(el.num)el.num.textContent=fmtQty(row.amount);
    if(el.sub){
      const net=row.netPerDay;
      el.sub.textContent = net===0 && row.producedPerDay===0
        ? '无产线'
        : `${net>=0?'+':'−'}${Math.abs(net)}/天`;
      el.sub.style.color = net>0?'var(--teal)':net<0?'var(--red)':'var(--muted)';
      el.sub.style.fontStyle='normal';
    }
    // 有了真实数据就摘掉「待接入」的灰化标记
    const box=el.num?.closest('.res');
    if(box)box.classList.remove('pending');
  }
}
/** 资源 id -> 顶栏标签。只列顶栏真正展示的那几种。 */
const RES_LABEL:Record<string,string>={ food:'食物', metal:'金属' };
/** 大数缩写：12600 -> 12.6k，与设计稿一致。 */
function fmtQty(n:number):string{
  return n>=10000 ? `${(n/1000).toFixed(1)}k` : String(n);
}
/* 电力顶栏投影。内核用瓦，UI 按量级自动选单位 —— 免得开局显示 0.0009 GW。 */
function fmtWatts(w:number):{v:string;u:string}{
  if(w>=1e9)return{v:(w/1e9).toFixed(2),u:' GW'};
  if(w>=1e6)return{v:(w/1e6).toFixed(2),u:' MW'};
  if(w>=1e3)return{v:(w/1e3).toFixed(1),u:' kW'};
  return{v:String(Math.round(w)),u:' W'};
}
/** 瓦 -> 带单位的可读文本（详情面板用，与顶栏同一套量级规则）。 */
function fmtWattsText(w:number):string{const f=fmtWatts(w);return f.v+f.u;}
/** 氧气与水顶栏投影。储备小时数由**模拟层**给出，UI 不自己拿储量除。 */
function onFluidChange(){
  const f=K.fluidSummary();
  const put=(lab:string,stored:number,cap:number,reserve:number)=>{
    const e=_resEls[lab]; if(!e)return;
    const pct=cap>0?Math.round(stored/cap*100):0;
    if(e.num)e.num.textContent=cap>0?String(pct):'--';
    if(e.unit)e.unit.textContent=cap>0?'%':'';
    if(!e.sub)return;
    if(cap===0){e.sub.textContent='无储罐';e.sub.style.color='var(--muted)';}
    else if(reserve<0){e.sub.textContent='充足';e.sub.style.color='var(--green)';}
    else{e.sub.textContent=`储备 ${reserve.toFixed(1)} 小时`;
      e.sub.style.color=reserve<6?'var(--red)':reserve<24?'var(--amber,#ffb347)':'var(--teal)';}
    _resEls[lab]!.num?.parentElement?.parentElement?.parentElement?.classList.remove('pending');
  };
  put('氧气',f.stored.oxygen,f.capacity.oxygen,K.fluidReserve('oxygen'));
  put('水',f.stored.water_clean,f.capacity.water_clean,K.fluidReserve('water_clean'));
}
function onPowerChange(){
  const e=_resEls['电力']; if(!e)return;
  const p=K.powerSummary();
  const f=fmtWatts(p.generationWatts);
  if(e.num)e.num.textContent=f.v;
  if(e.unit)e.unit.textContent=f.u;
  if(e.sub){
    const net=p.generationWatts-p.demandWatts;
    if(p.componentCount===0){e.sub.textContent='无电网';e.sub.style.color='var(--muted)';}
    else if(p.shedCount>0){e.sub.textContent=`限电 ${p.shedCount} 处`;e.sub.style.color='var(--red)';}
    else{const n=fmtWatts(Math.abs(net));
      e.sub.textContent=`${net>=0?'+':'−'}${n.v}${n.u} 余量`;
      e.sub.style.color=net>=0?'var(--green)':'var(--red)';}
  }
}
const resources=[
 {ic:"i-pop",lab:"人口",v:"0",s:"在岗 0"},
 {ic:"i-energy",lab:"电力",v:"0",u:" W",s:"无电网"},
 {ic:"i-life",lab:"氧气",v:"--",s:"待 G010",pending:true},
 {ic:"i-water",lab:"水",v:"--",s:"待 G010",pending:true},
 {ic:"i-food",lab:"食物",v:"0",s:"无产线"},
 {ic:"i-metal",lab:"金属",v:8742,s:"+91/天"},
 {ic:"i-sci",lab:"科研",v:"--",s:"待 G015",pending:true},
];
const topbar=document.getElementById('topbar');
resources.forEach(r=>{const d=document.createElement('div');d.className='res'+(r.pending?' pending':'');
  if(r.pending)d.title='该系统尚未接入模拟内核';
  // 数值单独包一层 .num：直接对 .val 写 textContent 会把单位 <small> 一起抹掉
  d.innerHTML=`<svg class="ico ic"><use href="#${r.ic}"/></svg><div><div class="lab">${r.lab}</div>
  <div class="val"><span class="num">${r.v}</span><small style="font-size:10px;color:var(--muted)">${r.u||""}</small></div><div class="sub">${r.s}</div></div>`;
  topbar.appendChild(d);
  _resEls[r.lab]={num:d.querySelector('.num'),unit:d.querySelector('small'),sub:d.querySelector('.sub')};
  if(r.lab==='金属')_metalEl=d.querySelector('.num');});

let curCat="生命", sel:AnyObj=null;
const catsEl=document.getElementById('cats'), gridEl=document.getElementById('grid');
CATS.forEach(c=>{const b=document.createElement('div');b.className='cat'+(c===curCat?' on':'');
  b.setAttribute('role','tab'); b.tabIndex=0;
  b.dataset.cat=c;
  b.innerHTML=`<svg class="ico"><use href="#${catIcons[c]}"/></svg>${c}`;
  b.onclick=()=>{curCat=c;document.querySelectorAll('.cat').forEach(x=>x.classList.remove('on'));b.classList.add('on');renderGrid();};
  catsEl.appendChild(b);});
/* 建造菜单图标：优先用 Blender 渲染的模型缩略图（thumbs/<type>.png），
   没有对应模型的条目回退到 emoji。缩略图由 _thumb.py 统一机位/布光生成。 */
const THUMB_OK=new Set(['oxygen','water','mine','power','science','warehouse','food','habitat',
                        'corewell','pressure','bunker',
                        'battery','solar','garrison','hub','refinery','medical','oxygentank','algae','industrial',
                        'core']);   // 地标，虽不可建造但菜单里同样给预览
function thumbFor(name){
  const t=NAME2TYPE[name];
  return (t&&THUMB_OK.has(t))?`thumbs/${t}.png`:null;
}
function iconHTML(name,icon){
  const src=thumbFor(name);
  return src?`<img class="bi-img" src="${src}" alt="${name}">`:`<div class="bi">${icon}</div>`;
}
// publicDir 是 apps/game/assets，其内容构建后位于站点根目录 —— 正确 URL 是
// thumbs/x.png。写成 assets/thumbs/ 只在 dev 下碰巧可用，生产构建 404。
function thumbByType(t:string){ return t&&THUMB_OK.has(t)?`thumbs/${t}.png`:null; }
/* 成本区随「当前选中 + 当前库存」双向刷新：
   每行显示 需求/库存，不足行标红；有任一不足则禁用放置按钮。 */
function refreshCosts(){
  const box=document.getElementById('insCosts'); if(!box||!sel)return;
  const lack=new Set(shortages(sel.c));
  box.innerHTML=Object.entries(sel.c||{}).map(([k,v])=>{
    const stocked=STOCK_KEYS.has(k);
    const bad=lack.has(k);
    const own=stocked?` <small style="color:${bad?'var(--red)':'var(--muted)'}">/ ${K.stock()[k]??0}</small>`:'';
    return `<div class="c"><span>${resNames[k]||k}</span><b class="${bad?'neg':''}" style="${bad?'color:var(--red)':''}">${v}${own}</b></div>`;
  }).join('') || '<div class="c" style="grid-column:1/-1">无建造成本</div>';
  const btn=document.getElementById('placeBtn') as HTMLButtonElement;
  btn.disabled=!!sel.lock||lack.size>0;
  btn.title=lack.size?('资源不足：'+[...lack].map(k=>resNames[k]||k).join('、')):'';
}
/* 工务工具模式。声明必须早于 renderGrid() —— 后者在模块初始化期就会跑到
   selectBuilding，而 selectBuilding 会读到这个变量。 */
let toolMode:string|null=null;

/**
 * 选中建造栏的一项。
 *
 * `autoPlace` 默认 true —— 点卡片即进入放置模式，省掉「选完再跑去右上角点按钮」
 * 那段行程（1920 宽屏上超过 1400px，触摸端要横跨整块屏）。
 *
 * **开局与切分类的自动选中必须传 false**：一是开局就把幽灵挂在鼠标上是错的手感；
 * 二是 renderGrid() 在模块初始化期执行，那时后面的模块级 let 尚未初始化，
 * 进放置会当场 TDZ 并中断整个模块 —— 本文件第七次踩这个坑
 * （前六次见 G014 报告第 9 节、G012 报告 4.1）。
 */
function selectBuilding(b:AnyObj,cell:HTMLElement,autoPlace=true){
  if(b.lock)return;
  sel=b;
  revealInspector();     // 竖屏下详情栏是抽屉，选了不展开就等于没反馈；宽屏无副作用
  document.querySelectorAll('.cell').forEach(x=>{x.classList.remove('sel');x.setAttribute('aria-selected','false');});
  cell.classList.add('sel'); cell.setAttribute('aria-selected','true');
  const src=thumbByType(b.t);
  document.getElementById('insIcon')!.innerHTML=src
    ? `<img class="bi-img" src="${src}" alt="">`
    : `<svg class="ico" style="font-size:30px;color:var(--cyan-dim)"><use href="#${catIcons[curCat]}"/></svg>`;
  document.getElementById('insTitle')!.textContent=b.n;
  document.getElementById('insCat')!.textContent=b.mk?`${curCat} · Mk.${b.mk}`:curCat;
  document.getElementById('insDesc')!.textContent=b.d;
  refreshCosts();
  /* 选中建造项时**取消**正在进行的放置：否则幽灵还是上一个建筑，与面板对不上。
     守卫用 window.__placingMode 而不是模块级 `placing` —— 本函数在模块初始化期
     （renderGrid）就会被调用，那时 `placing` 与 `ghost` 都还没声明，读它们会 TDZ。
     window 上的普通属性任何时候读都安全。

     **刻意不自动进入放置模式。** 试过，是个回归：玩家点完卡片就一直举着幽灵，
     再点场景里的建筑会变成「尝试建造」而不是「查看信息」—— 实测弹出的是
     「该类型不允许建在此环带」，信息卡永远出不来。放置的入口是建造栏右端
     那颗按钮，它就在卡片旁边，不需要长途跋涉。 */
  if(window.__placingMode)exitPlacing();
  void autoPlace;   // 保留参数：调用点已按语义区分「自动选中」与「玩家点击」
}
function renderGrid(){
  gridEl.innerHTML='';
  LIB[curCat].forEach((b:AnyObj)=>{
    const cell=document.createElement('div');
    cell.className='cell'+(b.lock?' lock':'');
    cell.setAttribute('role','option'); cell.setAttribute('aria-selected','false');
    cell.dataset.type=b.t;                     // e2e 稳定选择器，不依赖文案与位置
    if(!b.lock)cell.tabIndex=0;
    const src=thumbByType(b.t);
    // 每一项都用该建筑自己的模型渲染图，不再有 emoji 占位
    cell.innerHTML=(b.mk?`<span class="mk">MK.${b.mk}</span>`:'')
      +(b.lock?`<svg class="ico lk"><use href="#i-lock"/></svg>`:'')
      +(src?`<img class="bi-img" src="${src}" alt="" loading="lazy">`
           :`<svg class="ico bi-img" style="font-size:34px;color:var(--muted)"><use href="#${catIcons[curCat]}"/></svg>`)
      +`<div class="bn">${b.n}</div>`;
    cell.title=b.lock?`${b.n}（${b.lock}）`:b.n;
    cell.onclick=()=>selectBuilding(b,cell);
    cell.onkeydown=(e:KeyboardEvent)=>{if(e.key==='Enter'||e.key===' '){e.preventDefault();selectBuilding(b,cell);}};
    gridEl.appendChild(cell);
  });
  document.getElementById('buildCount')!.textContent=`${LIB[curCat].length} 项`;
  const first=LIB[curCat].find((b:AnyObj)=>!b.lock);
  // 开局/切分类的自动选中只更新详情卡，不进入放置模式
  if(first)selectBuilding(first,gridEl.children[LIB[curCat].indexOf(first)] as HTMLElement,false);
}
renderGrid();
// dock 条目是横向排布：把滚轮的纵向增量转成横向滚动（这就是「横向滚动」的手感来源）
gridEl.addEventListener('wheel',e=>{
  if(Math.abs(e.deltaY)>Math.abs(e.deltaX)){gridEl.scrollLeft+=e.deltaY;e.preventDefault();}
},{passive:false});

/* ================= 底部工具栏 =================
   只有【道路】与【拆除】是真正实现的工具；其余五项没有任何底层系统支撑，
   因此渲染成【诚实的禁用态】——不接受激活，点击直说"未开放"，
   而不是假装切换成功（AGENTS.md：禁止把占位按钮标记为完成）。 */
type ToolDef={ic:string;nm:string;mode?:string;tip?:string};
const TOOL_DEFS:ToolDef[]=[
  {ic:"i-logi", nm:"道路",  mode:"road",     tip:"沿环形网格铺设发光路面（可连续放置，Esc 退出）"},
  {ic:"i-energy",nm:"电力塔", mode:"pylon",   tip:"沿环带竖立屏蔽母线塔（金属 45/座，Esc 退出）"},
  {ic:"i-water", nm:"管道",   mode:"pipe",     tip:"铺设管网：建筑与相邻管道格自动接入，氧气与水共用一张网（金属 6/格，Esc 退出）"},
  {ic:"i-prod",  nm:"输送带", mode:"conveyor", tip:"沿环带架设封闭输送带（金属 35/格，Esc 退出）"},
  {ic:"i-energy",nm:"电缆",  mode:"cable",    tip:"沿环形网格铺设电缆，建筑与相邻电缆格自动接入电网（金属 4/格，Esc 退出）"},
  {ic:"i-water", nm:"隔离阀", mode:"valve",    tip:"在已有管道上开关隔离阀：关闭即切断该处管网，可阻断污染扩散（Esc 退出）"},
  {ic:"i-close", nm:"拆除",  mode:"demolish", tip:"点击场上建筑将其拆除并释放单元格（Esc 退出）"},
];
const toolsEl=document.getElementById('tools');
const toolEls:Record<string,HTMLElement>={};
TOOL_DEFS.forEach(d=>{
  const t=document.createElement('div');
  const usable=!!d.mode;
  t.className='tool'+(usable?'':' off');
  t.setAttribute('role','button');
  if(d.mode)t.dataset.tool=d.mode;             // e2e 稳定选择器
  t.setAttribute('title',usable?(d.tip||d.nm):`${d.nm} · 后续版本开放`);
  if(!usable)t.setAttribute('aria-disabled','true');
  t.innerHTML=`<svg class="ico ti"><use href="#${d.ic}"/></svg><span class="tn">${d.nm}</span>`;
  t.onclick=()=>{
    if(!usable){toast(`🚧 「${d.nm}」尚未实现，将在后续版本开放`);return;}
    setToolMode(toolMode===d.mode?null:d.mode!);
  };
  if(d.mode)toolEls[d.mode]=t;
  toolsEl.appendChild(t);
});

function setToolMode(mode:string|null){
  toolMode=mode;
  document.querySelectorAll('.tool').forEach(x=>x.classList.remove('on'));
  if(mode&&toolEls[mode])toolEls[mode].classList.add('on');
  if(mode)exitPlacing('');                     // 工具与建造放置互斥
  roadPreview.visible=(mode==='road');
  cablePreview.visible=(mode==='cable');
  pipePreview.visible=(mode==='pipe'||mode==='valve');
  infraPreview.visible=!!(mode&&INFRA_DEFS[mode]);
  if(!infraPreview.visible)infraPreview.clear();   // 预览克隆件即建即弃，退出就清
  if(hoverBldg&&hoverBldg!==selBldg){setHighlight(hoverBldg,false);hoverBldg=null;}
  renderer.domElement.style.cursor=mode?'crosshair':'default';
  if(mode==='road'){toast('🛣️ 道路模式：点击地面铺设路段 · Esc 退出');addLog('进入道路铺设模式');}
  else if(mode==='demolish'){toast('🧨 拆除模式：点击建筑将其拆除 · Esc 退出');addLog('进入拆除模式');}
  else if(mode&&INFRA_DEFS[mode]){toast(`🏗️ ${INFRA_DEFS[mode].label}模式：点击地面铺设 · Esc 退出`);addLog(`进入${INFRA_DEFS[mode].label}铺设模式`);}
  else toast('已退出工具模式');
}

/* ---------- 道路：与青色环网同族的发光路面 ----------
   道路挂在【环 × 扇区】网格上，由内核的 construction.roadCells 记账：
   它不占建筑单元格，因此永远不会跟建筑抢格；反过来已被建筑占用的
   单元格也不允许铺路（内核以 ROAD_CELL_OCCUPIED 拒绝），两套互不覆盖。
   这里只留渲染对象索引，用于拆除时移除网格。 */
const roadOkMat=new THREE.MeshBasicMaterial({color:0x53e08a,transparent:true,opacity:.45,
  side:THREE.DoubleSide,depthWrite:false});
const roadBadMat=new THREE.MeshBasicMaterial({color:0xff5d5d,transparent:true,opacity:.45,
  side:THREE.DoubleSide,depthWrite:false});
/* 沿某一环某一扇区的弧形贴地带。必须逐点采样 craterHeight——
   陨坑地形起伏很大，固定 y 的平面环在外环会整段埋进地里或悬空。 */
function roadRibbon(r:number,sct:number,width:number,yOff:number,mat:AnyObj){
  const rr=RING_R0+r*RING_STEP, n=SEC_OF(r);
  const a0=wrapSct(r,sct)/n*Math.PI*2, aw=Math.PI*2/n;
  const SEG=16, pos:number[]=[], idx:number[]=[];
  for(let i=0;i<=SEG;i++){
    const a=a0+aw*(i/SEG), ca=Math.cos(a), sa=Math.sin(a);
    for(const s of [-1,1]){
      const rad=rr+s*width/2, x=ca*rad, z=sa*rad;
      pos.push(x,craterHeight(x,z)+yOff,z);
    }
  }
  for(let i=0;i<SEG;i++){const b=i*2;idx.push(b,b+1,b+2,b+1,b+3,b+2);}
  const g=new THREE.BufferGeometry();
  g.setAttribute('position',new THREE.Float32BufferAttribute(pos,3));
  g.setIndex(idx); g.computeVertexNormals();
  return new THREE.Mesh(g,mat);
}
function roadSegment(r:number,sct:number,band:AnyObj,edge:AnyObj){
  const grp=new THREE.Group();
  grp.add(roadRibbon(r,sct,ROAD_W,.8,band));      // 路面辉光带
  grp.add(roadRibbon(r,sct,2.2,1.1,edge));        // 中心亮线，呼应放射干线
  return grp;
}
const roadPreview=new THREE.Group(); roadPreview.visible=false; scene.add(roadPreview);


let roadCell:{r:number;sct:number;key:string}|null=null, roadOk=false, roadWhy='';
function updateRoadPreview(x:number,z:number){
  const {r,sct}=worldToCell(x,z);
  const key=cellKey(r,sct);
  const taken=cellIsTaken(r,sct), laid=K.hasRoad(r,sct);
  roadOk=!taken&&!laid;
  roadWhy=taken?'该单元格已被建筑占用':(laid?'此处已有道路':'');
  roadCell={r,sct,key};
  roadPreview.clear();
  roadPreview.add(roadSegment(r,sct,roadOk?roadOkMat:roadBadMat,roadOk?roadOkMat:roadBadMat));
}
const ROAD_REJECT:Record<string,string>={
  ROAD_CELL_INVALID:'此处不是有效单元格',
  ROAD_CELL_OCCUPIED:'该单元格已被建筑占用',
  ROAD_ALREADY_EXISTS:'此处已有道路',
  ROAD_RESOURCES_INSUFFICIENT:'材料不足，无法铺路',
};
function placeRoad(){
  if(!roadCell)return;
  if(!roadOk){toast('⚠️ '+(roadWhy||'此处无法铺路'));return;}
  // 命令是唯一入口：预览通过≠命令通过，以内核结果为准
  const res=K.layRoad(roadCell.r,roadCell.sct);
  if(!res.ok){toast('⛔ '+(ROAD_REJECT[res.code||'']||res.code||'无法铺路'));return;}
  markInfraDirty();
  onStockChange();
  markDirty(); toast(`🛣️ 道路已铺设（第 ${roadCell.r+1} 环）`);
  addLog(`道路铺设完成 · 第 ${roadCell.r+1} 环 · 累计 ${countInfra(K.hasRoad)} 段`);
  roadOk=false; roadWhy='此处已有道路';
  roadPreview.clear();
  roadPreview.add(roadSegment(roadCell.r,roadCell.sct,roadBadMat,roadBadMat));
}

/* ---------- 基建铺设：电力塔 / 管道 / 输送带 ----------
   与道路同一套「环 × 扇区」记账（独立 Map，不与建筑抢格），但产物是
   真实 GLB 实例：段件（管道/输送带，12m）沿单元格弧长切向排 2~3 段
   首尾相接；点件（电力塔）每格一座立在弧中点。
   朝向直接用 faceCore：+Z 指核心时 +X 恰沿环切向 —— 段件自然连线。 */
const INFRA_DEFS:Record<string,{glb:string;cost:AnyObj;chain:boolean;label:string}>={
  pylon:   {glb:'pylon',   cost:{metal:45},        chain:false,label:'电力塔'},
  conveyor:{glb:'conveyor',cost:{metal:35,cell:1}, chain:true, label:'输送带'},
};
const SEG_LEN=12*BUILD_UPSCALE;                 // 段件世界长度（模型 12m × 全局放大）
const infraTaken=new Set<string>();             // "kind:ring:sector"
function infraSpots(kind:string,r:number,sct:number){
  // 单元格弧上的放置点：返回 [{x,z,rot}...]
  const rr=RING_R0+r*RING_STEP, n=SEC_OF(r);
  const a0=wrapSct(r,sct)/n*Math.PI*2, aw=Math.PI*2/n;
  const chain=INFRA_DEFS[kind].chain;
  const count=chain?Math.max(1,Math.round(rr*aw/SEG_LEN)):1;
  const out:Array<{x:number;z:number;rot:number}>=[];
  for(let i=0;i<count;i++){
    const a=a0+aw*((i+0.5)/count);
    const x=Math.cos(a)*rr, z=Math.sin(a)*rr;
    out.push({x,z,rot:faceCore(x,z)});
  }
  return out;
}
const infraPreview=new THREE.Group(); infraPreview.visible=false; scene.add(infraPreview);
let infraCell:{r:number;sct:number;key:string}|null=null, infraOk=false, infraWhy='';
function updateInfraPreview(kind:string,x:number,z:number){
  const {r,sct}=worldToCell(x,z);
  const key=kind+':'+cellKey(r,sct);
  const tpl=GLB_CACHE[INFRA_DEFS[kind].glb];
  infraCell={r,sct,key};
  const lack=shortages(INFRA_DEFS[kind].cost);
  const taken=cellIsTaken(r,sct), laid=infraTaken.has(key);
  infraOk=!taken&&!laid&&lack.length===0&&!!tpl;
  infraWhy=taken?'该单元格已被建筑占用':laid?`此处已有${INFRA_DEFS[kind].label}`
          :lack.length?('资源不足：'+lack.map(k=>resNames[k]||k).join('、')):(!tpl?'模型加载中':'');
  infraPreview.clear();
  if(!tpl)return;
  const mat=infraOk?GHOST_OK:GHOST_BAD;
  for(const sp of infraSpots(kind,r,sct)){
    const m=tpl.clone(true);
    m.scale.setScalar(BUILD_UPSCALE);
    m.position.set(sp.x,craterHeight(sp.x,sp.z),sp.z);
    m.rotation.y=sp.rot;
    m.traverse((o:AnyObj)=>{if(o.isMesh){o.material=mat;o.castShadow=false;}});
    infraPreview.add(m);
  }
}
function placeInfra(kind:string){
  if(!infraCell)return;
  if(!infraOk){toast('⚠️ '+(infraWhy||'此处无法铺设'));return;}
  const def=INFRA_DEFS[kind];
  const spots=infraSpots(kind,infraCell.r,infraCell.sct);
  for(const sp of spots){
    const a=new THREE.Group();
    a.scale.setScalar(BUILD_UPSCALE);
    a.position.set(sp.x,craterHeight(sp.x,sp.z),sp.z);
    a.rotation.y=sp.rot;
    a.userData.bType=def.glb;
    city.add(a);
    poolAlloc(def.glb,a);                     // 与建筑同一套实例池，一种材质一次提交
  }
  infraTaken.add(infraCell.key);
  K.spendResources(def.cost,'infrastructure:'+kind);   // 与建筑同一本账
  onStockChange();
  toast(`✅ ${def.label}已铺设（第 ${infraCell.r+1} 环）`);
  addLog(`${def.label}铺设完成 · 第 ${infraCell.r+1} 环 · 金属 -${def.cost.metal||0}`);
  const c=cellCenter(infraCell.r,infraCell.sct);
  updateInfraPreview(kind,c.x,c.z);           // 原地重刷：立即读出「已占用」红态
}

/* ---------- 拆除 ---------- */
function demolish(root:AnyObj){
  if(!root)return;
  if(root===core||root.userData.bType==='core'){toast('⛔ 陨核反应井是地标，不可拆除');return;}
  const d=BINFO[root.userData.bType];
  const name=d?d.name:(TYPE2NAME[root.userData.bType]||root.userData.bType||'建筑');
  // 单元格：优先用建造时记下的 key，缺失时按世界坐标反算
  let key=root.userData.cellKey as string|undefined;
  if(!key){const c=worldToCell(root.position.x,root.position.z);key=cellKey(c.r,c.sct);}
  // 等级牌 Sprite 必须从 tierSprites 里摘掉，否则每帧仍会被遍历更新，
  // 并且随父级一起脱离场景后依旧被数组持有 —— 泄漏 + 空中残留。
  const sprites:AnyObj[]=[];
  root.traverse((o:AnyObj)=>{if(o.isSprite)sprites.push(o);});
  for(const sp of sprites){
    const i=tierSprites.indexOf(sp);
    if(i>=0)tierSprites.splice(i,1);
    if(sp.parent)sp.parent.remove(sp);
    sp.material.dispose();                 // 贴图走缓存共享，只销毁材质
  }
  if(root===selBldg){clearSel();binfoEl.classList.remove('show');}
  else setHighlight(root,false);           // 还原 hover 高亮换上的克隆材质
  if(root===hoverBldg)hoverBldg=null;
  if(root.userData.slot!=null)poolFree(root);   // 归还实例槽位（尾槽换补）
  city.remove(root);
  // GLB 升级前的程序化占位体（visible=false）也一并移出，避免残留与延迟替换
  const sub=root.userData.standin;
  if(sub&&sub.parent)sub.parent.remove(sub);
  // 释放单元格与退款都由内核命令完成（按生命周期走取消或拆除）
  const eid=root.userData.entityId as string|undefined;
  const freed=eid?K.removeBuilding(eid).ok:false;
  onStockChange();
  const cost=K.costOf(root.userData.bType);
  const hasCost=Object.keys(cost).length>0;
  addLog(freed?`${name} 已拆除 · 第 ${Number(key.split(':')[0])+1} 环单元格已释放`:`${name} 已拆除`);
  markDirty();
  toast(`🧨 ${name} 已拆除${hasCost?'，返还 60% 材料':''}`);
}

/* 底部模式标签：切换时高亮并给出反馈（管理/政策/科技/数据为后续 Goal 的占位入口） */
document.querySelectorAll('.modes .mode').forEach(m=>{
  m.addEventListener('click',()=>{
    document.querySelectorAll('.modes .mode').forEach(x=>x.classList.remove('on'));
    m.classList.add('on');
    const name=(m as HTMLElement).dataset.m||m.textContent;
    if(name==='建造模式'){toast('🔨 建造模式：右侧选择建筑后点击放置');}
    else{toast(`「${name}」面板将在后续版本开放`);addLog(`切换到 ${name} 视图（预览）`);}
  });
});

/* ghost placement —— 预览必须是【这栋建筑本身】的等比副本。
   旧版是写死的 BoxGeometry(14,16,14)：实际建筑约 30~42 世界单位宽，
   预览只有 14，玩家看到的占位比真正放下去的小三倍。 */
const ghost=new THREE.Group();
ghost.visible=false; scene.add(ghost);
const GHOST_OK=new THREE.MeshBasicMaterial({color:0x53e08a,transparent:true,opacity:.40,depthWrite:false});
const GHOST_BAD=new THREE.MeshBasicMaterial({color:0xff5d5d,transparent:true,opacity:.40,depthWrite:false});
let ghostType=null;
/* 用真实 GLB 克隆做预览；GLB 尚未加载时退化成按该类型真实体积算的方盒，
   并在加载完成后自动换成模型。 */
function buildGhost(type){
  ghostType=type;
  ghost.clear();
  const tpl=type&&GLB_CACHE[type];
  if(tpl){
    const m=tpl.clone(true);
    m.scale.setScalar((GLB_BUILD_SCALE[type]||1)*BUILD_UPSCALE);
    m.traverse(o=>{if((o as AnyObj).isMesh){asMesh(o).material=GHOST_OK;o.castShadow=false;o.receiveShadow=false;}});
    ghost.add(m);
  }else{
    const sz=GHOST_FALLBACK_SIZE;
    const b=new THREE.Mesh(new THREE.BoxGeometry(sz,sz*.62,sz),GHOST_OK);
    b.position.y=sz*.31; ghost.add(b);
    if(type&&GLB_SRC[type]) ensureGLB(type,()=>{ if(ghostType===type) buildGhost(type); });
  }
}
const GHOST_FALLBACK_SIZE=34;      // 各 Mk.I/II 建筑世界占地的中位数
function setGhostValid(ok){
  const m=ok?GHOST_OK:GHOST_BAD;
  ghost.traverse(o=>{if((o as AnyObj).isMesh)asMesh(o).material=m;});
}
const PLAN_REJECT:Record<string,string>={
  BUILD_DEF_UNKNOWN:'该建筑尚无定义',
  BUILD_ANCHOR_INVALID:'位置无效',
  BUILD_ROTATION_INVALID:'朝向无效',
  BUILD_OUT_OF_BOUNDS:'超出可建区域',
  BUILD_CELL_OCCUPIED:'此单元格已被占用',
  BUILD_SLOPE_TOO_STEEP:'坡度过陡',
  BUILD_SUPPORT_TOO_LOW:'地基承重不足',
  BUILD_RESOURCES_INSUFFICIENT:'资源不足',
};
let placing=false, ghostYaw=0;
// R 键旋转预览朝向（45° 步进），并给出当前角度反馈
window.__rotateGhost=()=>{ghostYaw=(ghostYaw+Math.PI/4)%(Math.PI*2);
  toast(`🧭 朝向 ${Math.round(ghostYaw*180/Math.PI)}°`);};
const ray=new THREE.Raycaster(), ptr=new THREE.Vector2();
/* 预览吸附到最近的环×扇区单元格，并按分区规则/占用状态给出颜色反馈 */
let ghostCell=null, ghostReason='';
renderer.domElement.addEventListener('pointermove',e=>{
  if(!placing)return;
  ptr.set(e.clientX/innerWidth*2-1,-(e.clientY/innerHeight)*2+1);
  ray.setFromCamera(ptr,camera);
  if(!terrainPick(ray,_tpV))return;
  const hit={point:_tpV};                  // 下方沿用 hit.point 读法
  const btype=sel?sel.t:'habitat';
  if(btype!==ghostType) buildGhost(btype);
  const {r,sct}=worldToCell(hit.point.x,hit.point.z);
  const c=cellCenter(r,sct);
  ghost.position.set(c.x,craterHeight(c.x,c.z),c.z);      // 模型 pivot 在底面，直接贴地
  ghost.rotation.y=faceCore(c.x,c.z)+ghostYaw;           // 基准朝核心，R 叠加偏转
  const inRange=cellAllowed(btype,r);
  const free=!cellIsTaken(r,sct);
  ghostCell=(inRange&&free)?{r,sct,key:cellKey(r,sct)}:null;
  ghostReason=!inRange?'该类型不允许建在此环带':(!free?'此单元格已被占用':'');
  setGhostValid(!!ghostCell);
});
renderer.domElement.addEventListener('click',()=>{
  if(!placing||!ghost.visible)return;
  if(ghostCell){
    if(!sel){toast('请先在底部选择一个建筑');return;}
    const lack=shortages(sel.c);
    if(lack.length){toast('⛔ 资源不足：'+lack.map(k=>resNames[k]||k).join('、'));return;}
    // 内核在 Tick 内重新完整校验并原子落账：占格、扣料、建实体
    const rot=Math.round(ghostYaw/(Math.PI/4))&7;
    const res=K.planBuilding(sel.t,ghostCell.r,ghostCell.sct,rot);
    if(!res.ok){toast('⛔ '+(PLAN_REJECT[res.code||'']||res.code||'无法建造'));return;}
    const nb=building(ghost.position.x,ghost.position.z,1.15,sel.t,ghost.rotation.y);
    if(nb){nb.userData.cellKey=ghostCell.key;nb.userData.entityId=res.entityId;}
    onStockChange();
    markDirty(); toast(`🏗️ ${sel.n} 已开工（第 ${ghostCell.r+1} 环）· 施工中`);
    // 接线后放置只是【开工】：完工由内核的施工系统在若干 Tick 后达成
    addLog(`${sel.n} 已开工 · 第 ${ghostCell.r+1} 环 · 金属 -${sel.c?.metal||0}`);
    placing=false;window.__placingMode=false;ghost.visible=false;ghostCell=null;}
  else{
    /* 点在「已被占用」的格上，玩家的意图几乎总是**看那栋楼**，不是硬要建在这。
       原先只弹一句「此单元格已被占用」—— 一个死胡同：想查看信息必须先知道
       要按 Esc 退出放置，而界面没有任何地方写着这一点（用户原话：
       「点击木有用啊，不弹信息栏目」）。改为：命中已有建筑就退出放置并选中它。
       ptr/ray 与幽灵预览共享，pointermove 已把它们对准了光标。 */
    const hits=ray.intersectObjects([city,core],true);
    const hitRoot=pickFromHits(hits);
    if(hitRoot){
      exitPlacing();
      clearSel();
      selectSceneBuilding(hitRoot);
      return;
    }
    toast('⚠️ '+(ghostReason||'此处无法建造'));
  }
});
/**
 * 进入放置模式。唯一入口是建造栏右端的「放置建筑」按钮 ——
 * 曾试过「点卡片即进入」，被证实是回归（放置模式吞掉建筑点击，信息卡出不来），
 * 已撤回，见 32d9459。
 */
function enterPlacing(quiet=false){
  if(!sel)return false;
  if(toolMode)setToolMode(null);           // 建造与工具模式互斥
  placing=true;window.__placingMode=true;ghost.visible=true;ghostYaw=0;
  collapseDrawers();   // 让出场景给玩家选位置；宽屏无副作用
  buildGhost(sel.t||'habitat');
  if(!quiet)toast(`建造模式：移动选择位置，点击放置 · R 旋转 · 右键/Esc 退出`);
  return true;
}
document.getElementById('placeBtn').onclick=()=>{
  if(!sel){toast("请先在下方建造栏选择一个建筑");return;}
  enterPlacing();
};
// 放置模式下右键 = 取消
function exitPlacing(msg?:string){placing=false;window.__placingMode=false;ghost.visible=false;if(msg)toast(msg);}
addEventListener('pointerdown',e=>{if(placing&&e.button===2)exitPlacing('已退出建造模式');});
// 放置模式下 Esc = 取消（在回中逻辑之前拦截）
addEventListener('keydown',e=>{if(e.code==='Escape'&&placing){exitPlacing('已退出建造模式');e.preventDefault();e.stopImmediatePropagation();}},true);
// 工具模式下 Esc / 右键 = 退出（与建造模式一致的退出手感）
addEventListener('keydown',e=>{if(e.code==='Escape'&&!placing&&toolMode){setToolMode(null);e.preventDefault();e.stopImmediatePropagation();}},true);
addEventListener('pointerdown',e=>{if(!placing&&toolMode&&e.button===2)setToolMode(null);});


/* ================= 建筑等级标注 =================
   升级不做新建模（成本过高），改用头顶等级牌：Canvas 贴图 Sprite。
   Sprite 始终朝向相机，且按距离淡出，避免全景时糊成一片光点。 */
const _tierTexCache = new Map();
function tierTexture(tier, color){
  const k = tier + ':' + color;
  if (_tierTexCache.has(k)) return _tierTexCache.get(k);
  const cv = document.createElement('canvas'); cv.width = 128; cv.height = 64;
  const g = cv.getContext('2d');
  g.fillStyle = 'rgba(8,14,20,.82)';
  g.beginPath(); g.roundRect(4, 8, 120, 44, 8); g.fill();
  g.strokeStyle = '#' + color.toString(16).padStart(6, '0');
  g.lineWidth = 3; g.stroke();
  g.fillStyle = '#' + color.toString(16).padStart(6, '0');
  g.font = 'bold 30px system-ui, sans-serif';
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText('Mk.' + ['I', 'II', 'III'][Math.min(tier, 2)], 64, 31);
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  _tierTexCache.set(k, t);
  return t;
}
const tierSprites = [];
function attachTier(root, tier, color){
  const mat = new THREE.SpriteMaterial({ map: tierTexture(tier, color), transparent: true, depthTest: true });
  const sp = new THREE.Sprite(mat);
  // 实例化建筑的锚点没有网格，Box3 会是空盒（-Infinity）；高度从池模板取
  const pool = POOLS[root.userData.bType];
  let hLocal;
  if (root.userData.slot != null && pool) hLocal = pool.topY;
  else {
    const box = new THREE.Box3().setFromObject(root);
    hLocal = (box.max.y - box.min.y) / Math.max(root.scale.y, .001);
  }
  sp.position.set(0, hLocal + 6, 0);
  // 尺寸每帧按距离重算（见 updateTierSprites）。这里只记录父级缩放：
  // 牌子挂在被 TYPE_SCALE 缩放过的 root 下，世界尺寸会被父级乘一遍。
  sp.userData.pscale = Math.max(root.scale.x, .001);
  root.add(sp);
  tierSprites.push(sp);
  root.userData.tier = tier;
  return sp;
}
/* 恒定屏幕尺寸 + 距离淡出。
   固定世界尺寸（原来是 16×8）会随透视放大：贴近建筑时牌子能盖住半个屏幕，
   互相叠压。Sprite 想保持恒定像素大小，世界尺寸必须【正比于相机距离】：
   距离 d 处满屏高度 = 2·d·tan(fov/2)，取其 TIER_H 比例即可。 */
const TIER_H = 0.036;                       // 牌高占屏幕高度的比例（≈40px @1080p）
const _tanHalfFov = Math.tan(44 * Math.PI / 360);
function updateTierSprites(){
  const camPos = camera.position;
  for (const sp of tierSprites){
    const d = sp.getWorldPosition(_tmpV).distanceTo(camPos);
    const a = d > 260 ? 0 : (d > 180 ? (260 - d) / 80 : 1);
    sp.material.opacity = a;
    sp.visible = a > 0.02;
    if (!sp.visible) continue;
    // 除以父级缩放，抵消 root 上的 TYPE_SCALE，否则大建筑的牌子跟着变大
    const hh = d * 2 * _tanHalfFov * TIER_H / sp.userData.pscale;
    sp.scale.set(hh * 2, hh, 1);            // 贴图 128×64，宽高比 2:1
  }
}
const _tmpV = new THREE.Vector3();

/* 类型信标按【缩放距离】淡出。
   信标的作用是俯视时快速分辨建筑类型，但拉到全景时 147 个色点只剩噪声 ——
   而那个距离上建筑本来就成片，看的是布局不是个体。近距在某片区作业时才真正
   需要扫类型，所以近显远隐。与头顶等级牌同一套思路（updateTierSprites），
   区别是信标是实例化网格、无法逐实例调透明度，故按整体缩放档位统一淡出。 */
const BEACON_FULL = 300;    // 此距内全显
const BEACON_GONE = 480;    // 此距外全隐（默认机位 640 -> 不显示）
function updateBeacons(){
  const d = camCtl.dist;
  const a = d <= BEACON_FULL ? 1 : (d >= BEACON_GONE ? 0 : (BEACON_GONE - d) / (BEACON_GONE - BEACON_FULL));
  for (const type in POOLS){
    const rec = POOLS[type].beaconRec;
    if (!rec) continue;
    const im = rec.im;
    const vis = a > 0.02;
    if (im.visible !== vis) im.visible = vis;
    if (vis) im.material.opacity = a;
  }
}

/* ================= BUILDING INFO (click interaction) ================= */
const BINFO={
  core:{name:"陨核反应井",en:"Crater Core",icon:"☢️",cat:"地标 · 能源核心",
    desc:"殖民地的能量心脏。利用陨核深处的地热能进行可控聚变反应，为整座城市提供基础电力与热量。磁暴期间输出会出现波动。",
    yield:[["电力","+1.8 GW","pos"],["热量","+420 TJ","pos"],["稀有同位素","+3/天","pos"]]},
  habitat:{name:"居住穹顶",en:"Habitat Dome",icon:"🛖",cat:"生命 · 居住",
    desc:"加压密封居住单元，为殖民者提供温控、辐射屏蔽与基础生活空间。多个穹顶通过加压廊桥互联。",
    yield:[["居住容量","+64 人","pos"],["电力","-18 MW","neg"],["氧气","-12/天","neg"]]},
  industrial:{name:"制造车间",en:"Fabrication Plant",icon:"🏭",cat:"生产 · 工业",
    desc:"通用重工业厂房，将金属与合金加工成结构件、管道与机械备件。是殖民地扩张的物质基础。",
    yield:[["结构件","+9/天","pos"],["金属","-46/天","neg"],["电力","-95 MW","neg"]]},
  oxygen:{name:"制氧厂",en:"Oxygen Plant",icon:"🫧",cat:"生命 · 支持",
    desc:"从稀薄大气中分离并加压氧气，经由管网输送至各居住与工作单元，是维持人口存续的第一生命线。",
    yield:[["氧气","+58/天","pos"],["电力","-72 MW","neg"],["水","-6/天","neg"]]},
  power:{name:"储能电站",en:"Power Storage",icon:"🔋",cat:"能源 · 缓冲",
    desc:"大型线圈储能阵列，平抑电网峰谷并在磁暴或反应堆停机时提供黑启动能力。",
    yield:[["储能容量","+240 MWh","pos"],["电力","缓冲","pos"],["金属","维护","neg"]]},
  water:{name:"钻水站",en:"Water Bore",icon:"💧",cat:"生命 · 支持",
    desc:"深井钻探设备，开采陨坑地下冰层并初步净化，供给饮用、农业与工业冷却。",
    yield:[["水","+34/天","pos"],["电力","-38 MW","neg"]]},
  food:{name:"温室农场",en:"Greenhouse",icon:"🌱",cat:"生产 · 食物",
    desc:"封闭式水培温室，在人工光照下培育作物与藻类，是殖民地食物自给的主要来源。",
    yield:[["食物","+26/天","pos"],["水","-14/天","neg"],["电力","-30 MW","neg"]]},
  science:{name:"科研站",en:"Research Post",icon:"🔬",cat:"科技 · 研究",
    desc:"配备雷达与样本分析实验室的前哨研究站，解锁新技术并提升全殖民地效率。",
    yield:[["科研点","+12/天","pos"],["电力","-25 MW","neg"]]},
  refinery:{name:"精炼厂",en:"Refinery",icon:"⚗️",cat:"生产 · 精炼",
    desc:"将原矿高温精炼为可用金属与稀有合金，是采矿与制造之间的关键环节。",
    yield:[["金属","+22/天","pos"],["原矿","-40/天","neg"],["电力","-88 MW","neg"]]},
  mine:{name:"采矿井",en:"Mine Shaft",icon:"⛏️",cat:"生产 · 采掘",
    desc:"深层矿井头架，开采陨坑壁富含的金属与原矿，是资源链的最上游。",
    yield:[["原矿","+31/天","pos"],["电力","-52 MW","neg"],["结构磨损","持续","neg"]]},
  warehouse:{name:"仓储中心",en:"Warehouse",icon:"📦",cat:"物流 · 仓储",
    desc:"平顶仓储与集装箱堆场，提升殖民地整体库存上限并加快物资周转。",
    yield:[["库存上限","+500","pos"],["电力","-8 MW","neg"]]},
  garrison:{name:"安保哨塔",en:"Garrison",icon:"🛡️",cat:"秩序 · 安保",
    desc:"武装警戒哨塔，维护殖民地区域秩序，应对骚乱与外部威胁。",
    yield:[["秩序","+6","pos"],["电力","-15 MW","neg"],["人员","-4","neg"]]},
  hub:{name:"指挥塔",en:"Command Hub",icon:"🗼",cat:"地标 · 指挥",
    desc:"殖民地中枢指挥塔，协调全城资源调度、危机响应与对外通讯，是管理层的神经中枢。",
    yield:[["协调效率","+10%","pos"],["电力","-40 MW","neg"]]},
};
const binfoEl=document.getElementById('binfo');
const $=id=>document.getElementById(id);
const biFields={title:$('biTitle'),name:$('biName'),en:$('biEn'),chip:$('biChip'),icon:$('biIcon'),desc:$('biDesc'),stats:$('biStats'),yield:$('biYield')};
document.getElementById('biClose').onclick=()=>{binfoEl.classList.remove('show');clearSel();};
/* 生命周期 -> 中文与色彩。取自内核，不再是写死的「运转状态 正常」。
   供电/人员曾经也在这里写死成「已接入」「在岗」—— 那是假数据：
   电力与人口系统要到 G009 / G012 才存在。没有的系统就不显示，不糊弄。 */
const LIFECYCLE_TEXT:Record<string,[string,string]>={
  planned:['计划中','warn'], under_construction:['施工中','warn'],
  operational:['运转中','pos'], damaged:['受损','neg'],
};
let selBldg=null, hoverBldg=null;
/* highlight by CLONING materials on the target building only — buildings share
   materials, so mutating a shared material would light up every building that
   uses it. We swap in per-mesh clones, tint them, then restore the originals. */
const _origMats=new WeakMap(); // mesh -> original (shared) material
/* 实例高亮色：instanceColor 是乘算，用 >1 分量提亮。
   与旧 emissive 值一一对应：hover 冷青 / 拆除红 / 选中亮青。 */
const _TINT_HOVER=new THREE.Color(1.25,1.5,1.7);
const _TINT_DEMO=new THREE.Color(1.9,0.55,0.55);
const _TINT_SEL=new THREE.Color(1.35,1.75,2.05);
function setHighlight(root:AnyObj,on:boolean,color?:number){
  if(root.userData&&root.userData.slot!=null){
    poolTint(root,on?(color===0x6a1f22?_TINT_DEMO:color===0x3f8fbf?_TINT_SEL:_TINT_HOVER):null);
    return;
  }
  root.traverse(o=>{
    if(!o.isMesh)return;
    if(on){
      if(!_origMats.has(o))_origMats.set(o,o.material);
      const base=_origMats.get(o);
      const clone=Array.isArray(base)?base.map(m=>m.clone()):base.clone();
      const mats=Array.isArray(clone)?clone:[clone];
      // bloom 阈值是 0.5；原来把 emissiveIntensity 推到 0.55 刚好越过阈值，
      // 整栋建筑被 bloom 吃掉糊成一团光斑，完全看不出形状。
      // 压到 0.34 以下：能明确读出"被选中"，但不触发泛光。
      mats.forEach(m=>{if('emissive' in m){m.emissive.setHex(color);m.emissiveIntensity=on===true?.34:.2;}});
      o.material=clone;
    }else{
      const orig=_origMats.get(o);
      if(orig){
        const cur=Array.isArray(o.material)?o.material:[o.material];
        cur.forEach(m=>m.dispose&&m.dispose());
        o.material=orig;
        _origMats.delete(o);
      }
    }
  });
}
function clearSel(){if(selBldg){setHighlight(selBldg,false);selBldg=null;}}
function showBuildingInfo(root){
  const type=root.userData.bType;
  const d=BINFO[type]||{name:TYPE2NAME[type]||type,en:"",icon:"▦",cat:"未知",desc:"暂无资料。",yield:[]};
  biFields.title.textContent=d.name;
  biFields.name.textContent=d.name;
  biFields.en.textContent=d.en;
  biFields.chip.textContent=d.cat;
  // 世界建筑详情同样用模型渲染图，与建造菜单是同一套视觉
  const _s=thumbByType(type);
  biFields.icon.innerHTML=_s?`<img class="bi-img" src="${_s}" alt="">`
    :`<svg class="ico"><use href="#i-logi"/></svg>`;
  biFields.desc.textContent=d.desc;
  renderBuildingStats(root);
  /* 产出/消耗表原本整张是手写的示意数字（温室写着「电力 -30 MW」，实际耗电 400 W）。
     G009 落地后它就和真数据并排显示，矛盾刺眼。处理原则与顶栏一致：
     有内核数据的用真值，没有的明确标注待接入，绝不保留伪造数字。 */
  const u2=K.utilityOf(type);
  const yieldRows:string[][]=[];
  if(u2){
    if(u2.outputWatts>0)yieldRows.push(['发电',fmtWattsText(u2.outputWatts),'pos']);
    if(u2.drawWatts>0)yieldRows.push(['耗电',fmtWattsText(u2.drawWatts),'neg']);
    if(u2.storagePowerWatts>0)
      yieldRows.push(['储能',`${fmtWattsText(u2.storagePowerWatts)} / ${Math.round(u2.storageCapacityWattMinutes/60)} W·h`,'pos']);
  }
  // 其余产出（食物/水/氧气/科研…）要到 G010–G015 才有真实来源
  for(const [k] of d.yield){
    if(k==='电力'||k==='储能容量')continue;              // 电力已由上面的真值覆盖
    yieldRows.push([k,'待接入','pending']);
  }
  biFields.yield.innerHTML=yieldRows.length
    ? yieldRows.map(([k,v,c])=>`<div class="c"><span>${k}</span><b class="${c||''}">${v}</b></div>`).join('')
    : '<div class="c" style="grid-column:1/-1;color:var(--muted)">暂无产出数据</div>';
  binfoEl.classList.add('show');
}

/* 运行状态一律取自内核实体；地标（陨核反应井等）不是实体，标注为地标而非伪造状态。
   工地状态会随 Tick 变化，因此这段必须能被反复调用 —— 面板是活投影，不是快照。 */
function renderBuildingStats(root:AnyObj){
  const type=root.userData.bType;
  const idTag=type==='core'?'核心 · 唯一':'# '+String(root.userData.bId).padStart(3,'0');
  const eid=root.userData.entityId as string|undefined;
  const bc=eid?K.buildingOf(eid):undefined;
  const rows:string[][]=[];
  if(bc){
    const [txt,cls]=LIFECYCLE_TEXT[bc.lifecycle]||[bc.lifecycle,''];
    rows.push(['运行状态',txt,cls]);
    if(bc.lifecycle==='under_construction'||bc.lifecycle==='planned'){
      const pct=Math.round(bc.progressMinutes/bc.workMinutes*100);
      rows.push(['施工进度',`${pct}%`,'warn']);
      const o=K.outlookOf(eid!);
      if(o){
        const gap=Object.entries(o.undelivered).map(([k,v])=>`${resNames[k]||k} ${v}`).join(' · ');
        rows.push(['材料缺口',gap||'无',gap?'warn':'pos']);
        rows.push(['预计完成',bc.blocked?'—':`约 ${o.etaTicks} 分钟`,bc.blocked?'neg':'']);
      }
      if(bc.blocked)rows.push(['阻塞原因',bc.blocked,'neg']);
    }else{
      rows.push(['完好度','100%','pos']);
    }
    rows.push(['所在单元',`第 ${bc.anchor.ring+1} 环 · ${bc.anchor.sector} 号`,'']);
    /* 供电状态：G008A 时特意删掉了写死的「已接入」，现在用内核真数据加回来。
       红线是 —— 只有内核确实认识这栋楼的电力属性时才显示这一行。 */
    if(eid&&K.defOf(bc.defId)){
      const pw=K.powerOf(eid);
      const u=K.utilityOf(bc.defId);
      const draws=(u?.drawWatts??0)>0;
      const gens=(u?.outputWatts??0)>0||(u?.storagePowerWatts??0)>0;
      if(draws||gens){
        rows.push(['供电状态',
          !pw.connected?'未接入电网':(pw.shed?'限电中 · 已切断':'已接入'),
          !pw.connected?'warn':(pw.shed?'neg':'pos')]);
      }
      /* 电力优先级：G009 已实现 shedding，但玩家不可调。先占位展示当前值。 */
      if(draws||gens){
        const prio=u?.sheddingPriority??50;
        const prioTxt=prio>=80?'Critical':prio>=60?'High':prio>=40?'Normal':'Low';
        rows.push(['电力优先级',`${prioTxt} (${prio})`,'']);
      }
    }
    /* 人员配置（G012）与产线状况（G011）—— 都取自内核，不再是占位。 */
    const jobs=K.jobsOf(eid!);
    if(jobs.length>0){
      const filled=jobs.filter(j=>j.citizenId).length;
      const onDuty=jobs.filter(j=>j.onShift).length;
      rows.push(['人员配置',`${filled}/${jobs.length} 在册 · ${onDuty} 在岗`,
        filled<jobs.length?'warn':(onDuty>0?'pos':'')]);
      /* 效率分解逐项展开（验收第 4 条）。取在岗者的均值 —— 逐人列出来在
         详情卡里放不下，且玩家关心的是「这栋楼现在的效率」。
         每一项都来自内核的 EffectivenessBreakdown，UI 不自己算。 */
      const onList=jobs.filter(j=>j.onShift&&j.effectiveness!=null);
      if(onList.length>0){
        const avg=(pick:(v:number)=>number)=>Math.round(
          onList.reduce((n,j)=>n+pick(j.effectiveness!),0)/onList.length/100);
        rows.push(['综合效率',`${avg(v=>v)}%`,avg(v=>v)>=80?'pos':avg(v=>v)>=50?'warn':'neg']);
      }
      const staffNames=jobs.filter(j=>j.name).slice(0,3).map(j=>j.name).join('、');
      if(staffNames)rows.push(['在册人员',staffNames+(filled>3?` 等 ${filled} 人`:''),'']);
    }
    const pr=K.productionOf(eid!);
    const spec=K.productionSpecOf(bc.defId);
    if(spec&&spec.recipes.length>0){
      rows.push(['产线状态',
        pr.blocked?K.PRODUCTION_BLOCK_TEXT[pr.blocked]:'运转中',
        pr.blocked?'neg':'pos']);
      const out=Object.entries(pr.outputs).filter(([,v])=>v>0)
        .map(([k,v])=>`${resNames[k]||k} ${v}`).join(' · ');
      if(out)rows.push(['待运出',out,'']);
    }
  }else{
    rows.push(['运行状态','地标 · 非建造实体','']);
  }
  biFields.stats.innerHTML=rows.map(([k,v,c])=>`<div class="c"><span>${k}</span><b class="${c}">${v}</b></div>`).join('')
    +`<div class="c"><span>单元编号</span><b>${idTag}</b></div>`;
}
/* 详情面板开着时每 500ms 重投影一次：施工进度、缺口与阻塞原因都会随 Tick 变，
   只在选中瞬间渲染一次会让玩家盯着一个永远不动的 1%。 */
setInterval(()=>{
  if(selBldg&&binfoEl.classList.contains('show'))renderBuildingStats(selBldg);
},500);
// 从被击中的 mesh 向上找拥有 pickRoot 的建筑根（GLB 建筑的子 mesh 没有 pickRoot，需向上回溯）
function pickRootOf(obj){let o=obj;while(o){if(o.userData&&o.userData.pickRoot)return o.userData.pickRoot;o=o.parent;}return null;}
/* 命中列表 -> 建筑根。实例池的 mesh 命中带 instanceId，
   由 池.records[instanceId] 直接得到锚点；程序化建筑仍走 pickRoot 回溯。 */
function pickFromHits(hits:AnyObj[]){
  for(const h of hits){
    const o=h.object;
    if(o.isInstancedMesh&&o.userData.poolType!==undefined){
      if(h.instanceId==null)continue;
      const rec=POOLS[o.userData.poolType]?.records[h.instanceId];
      if(rec)return rec;
      continue;
    }
    const r=pickRootOf(o); if(r)return r;
  }
  return null;
}
/* hover + click picking (shares the ghost raycaster/pointer) */
renderer.domElement.addEventListener('pointermove',e=>{
  if(placing)return;
  ptr.set(e.clientX/innerWidth*2-1,-(e.clientY/innerHeight)*2+1);
  ray.setFromCamera(ptr,camera);
  if(toolMode==='road'){                    // 道路模式：预览吸附到最近单元格
    if(terrainPick(ray,_tpV))updateRoadPreview(_tpV.x,_tpV.z);
    return;
  }
  if(toolMode==='cable'){                   // 电缆模式：同道路，账本同样在内核
    if(terrainPick(ray,_tpV))updateCablePreview(_tpV.x,_tpV.z);
    return;
  }
  if(toolMode==='pipe'||toolMode==='valve'){
    if(terrainPick(ray,_tpV))updatePipePreview(_tpV.x,_tpV.z);
    return;
  }
  if(toolMode&&INFRA_DEFS[toolMode]){       // 基建模式：塔/管/带
    if(terrainPick(ray,_tpV))updateInfraPreview(toolMode,_tpV.x,_tpV.z);
    return;
  }
  const hits=ray.intersectObjects([city,core],true);
  const root=pickFromHits(hits);
  if(root!==hoverBldg){
    if(hoverBldg&&hoverBldg!==selBldg)setHighlight(hoverBldg,false);
    hoverBldg=root;
    // 拆除模式下用红色 hover，明确"点下去会没"
    if(hoverBldg&&hoverBldg!==selBldg)setHighlight(hoverBldg,true,toolMode==='demolish'?0x6a1f22:0x2a4a5c);
    renderer.domElement.style.cursor=hoverBldg?'pointer':(toolMode?'crosshair':'default');
  }
});
renderer.domElement.addEventListener('click',e=>{
  if(placing)return;                       // 放置模式下不触发选中
  if(overHudPanel(e.clientX,e.clientY))return; // 光标真正落在 HUD 面板上才不选中
  ptr.set(e.clientX/innerWidth*2-1,-(e.clientY/innerHeight)*2+1);
  ray.setFromCamera(ptr,camera);
  if(toolMode==='road'){                   // 道路模式：点击即铺设，可连续放置
    if(terrainPick(ray,_tpV)){updateRoadPreview(_tpV.x,_tpV.z);placeRoad();}
    return;
  }
  if(toolMode==='cable'){
    if(terrainPick(ray,_tpV)){updateCablePreview(_tpV.x,_tpV.z);placeCable();}
    return;
  }
  if(toolMode==='pipe'){
    if(terrainPick(ray,_tpV)){updatePipePreview(_tpV.x,_tpV.z);placePipe();}
    return;
  }
  if(toolMode==='valve'){
    if(terrainPick(ray,_tpV)){updatePipePreview(_tpV.x,_tpV.z);toggleValve();}
    return;
  }
  if(toolMode&&INFRA_DEFS[toolMode]){
    if(terrainPick(ray,_tpV)){updateInfraPreview(toolMode,_tpV.x,_tpV.z);placeInfra(toolMode);}
    return;
  }
  if(toolMode==='demolish'){
    const dh=ray.intersectObjects([city,core],true);
    const target=pickFromHits(dh);
    if(target)demolish(target); else toast('🧨 拆除模式：请点击一栋建筑');
    return;
  }
  const hits=ray.intersectObjects([city,core],true);
  const root=pickFromHits(hits);
  clearSel();
  if(root)selectSceneBuilding(root);
  else binfoEl.classList.remove('show');
});

/** 选中场景里的一栋建筑：高亮 + 信息卡 + 镜头聚焦。普通点击与放置模式共用。 */
function selectSceneBuilding(root:AnyObj){
  selBldg=root;
  setHighlight(root,true,0x3f8fbf);
  showBuildingInfo(root);
  // 镜头平滑聚焦到选中建筑（保持舒适观察距离）
  focusOn(root.position.x,root.position.z,Math.max(camCtl.tDist,300));
  const d=BINFO[root.userData.bType];
  toast(`🔍 ${d?d.name:'建筑'}`);
}

// 几何遮挡检测：仅当光标落在某个常驻 HUD 面板的可见矩形内才算"点在 UI 上"。
// 不再用 e.target.closest 判断——那会被全屏/常驻面板误判，导致建筑点不中。
const HUD_BLOCK_SEL='.panel,#logo,#build,#binfo,#inspector,#minimap,.modes';
function overHudPanel(x,y){
  for(const el of document.querySelectorAll(HUD_BLOCK_SEL)){
    const cs=getComputedStyle(el);
    if(cs.display==='none'||cs.visibility==='hidden'||+cs.opacity===0)continue;
    const r=el.getBoundingClientRect();
    if(r.width===0||r.height===0)continue;
    if(x>=r.left&&x<=r.right&&y>=r.top&&y<=r.bottom)return true;
  }
  return false;
}

/* toast & log */
const toastEl=document.getElementById('toast');let toastT;
function toast(m:string){toastEl.textContent=m;toastEl.style.opacity="1";clearTimeout(toastT);toastT=setTimeout(()=>toastEl.style.opacity="0",2400);}
const logEl=document.getElementById('log'), clockEl=document.getElementById('clock');
[["18:03","地下层 勘探小队已返回"],["18:25","工业区 生产效率 +5%"],["18:47","磁暴探测器发现异常波动"],["19:12","居住区 新居民到达：+256"],["19:31","资源运输船已抵达物流枢纽"]].forEach(([t,m])=>addLog(m,t));
function addLog(msg:string,t?:string){const d=document.createElement('div');d.innerHTML=`<b>${t||clockEl.textContent}</b>${msg}`;
  logEl.prepend(d);while(logEl.children.length>7)logEl.lastChild.remove();}

/* clock / sim */
const dayEl=document.getElementById('dayNum');
/* 时钟是内核的：这里只投影。acc 累积真实耗时决定【何时】推进 Tick，
   推进量本身是固定步长 —— 模拟结果不依赖墙钟。 */
let speed=1,acc=0;
/** 外壳（主菜单/暂停菜单）要求的暂停。与玩家的倍速暂停是两回事。 */
let shellPaused=true;
let gameMin=K.clock().minuteOfDay, day=K.clock().day;
document.getElementById('speed')!.addEventListener('click',e=>{const b=(e.target as HTMLElement).closest('button');if(!b)return;
  speed=+b.dataset.s;document.querySelectorAll('.speed button').forEach(x=>x.classList.remove('on'));b.classList.add('on');});
let storm=2*3600+16*60+16;
setInterval(()=>{storm=Math.max(0,storm-1);
  const h=String(Math.floor(storm/3600)).padStart(2,'0'),m=String(Math.floor(storm%3600/60)).padStart(2,'0'),s=String(Math.floor(storm%60)).padStart(2,'0');
  document.getElementById('stormCd').textContent=`${h}:${m}:${s}`;},1000);

/* minimap */
const mm=(document.getElementById('mapCanvas') as HTMLCanvasElement).getContext('2d')!;
(function drawMap(){const c=95;mm.clearRect(0,0,190,190);
  mm.fillStyle='rgba(8,18,26,.9)';mm.beginPath();mm.arc(c,c,90,0,7);mm.fill();
  mm.strokeStyle='rgba(64,150,190,.5)';mm.beginPath();mm.arc(c,c,88,0,7);mm.stroke();
  mm.strokeStyle='rgba(64,150,190,.25)';[60,38].forEach(r=>{mm.beginPath();mm.arc(c,c,r,0,7);mm.stroke();});
  [[-40,-20,'#2fe0c0'],[30,35,'#a06bff'],[-10,50,'#53e08a'],[45,-40,'#ffb347']].forEach(([x,z,col])=>{
    mm.fillStyle=(col as string)+'55';mm.beginPath();mm.arc(c+(x as number)*.9,c+(z as number)*.9,16,0,7);mm.fill();mm.strokeStyle=col as string;mm.stroke();});
  mm.fillStyle='#ff9040';mm.beginPath();mm.arc(c,c,4,0,7);mm.fill();
  mm.fillStyle='#fff';mm.beginPath();mm.arc(c,c,1.5,0,7);})();
// 小地图点击 → 镜头定位（把 minimap 像素映射到城市坐标，±900 对应半径 90px）
const mapCanvasEl=document.getElementById('mapCanvas');
mapCanvasEl.style.cursor='crosshair';
mapCanvasEl.addEventListener('pointerdown',e=>{
  const r=mapCanvasEl.getBoundingClientRect();
  const px=(e.clientX-r.left)/r.width*190, py=(e.clientY-r.top)/r.height*190;
  const wx=(px-95)/90*900, wz=(py-95)/90*900;
  focusOn(wx,wz);
  toast(`🗺️ 已定位 (${Math.round(wx)}, ${Math.round(wz)})`);
});

/* ================= LOOP ================= */
addEventListener('resize',()=>{camera.aspect=innerWidth/innerHeight;camera.updateProjectionMatrix();
  renderer.setSize(innerWidth,innerHeight);composer.setSize(innerWidth,innerHeight);});
// 尺寸看门狗：预览面板隐藏→显示时，画布与 bloom 的 render target 可能被压成 0×0
// 且不再触发 resize。three 的 composer 不会自动重建内部 target，导致输出全黑。
// 每帧检查一次 renderer 实际尺寸与窗口是否一致，不一致就重建 renderer+composer 尺寸。
function ensureSize(){
  const w=innerWidth,h=innerHeight;
  const sz=renderer.getSize(new THREE.Vector2());
  if(sz.x===w&&sz.y===h)return;
  camera.aspect=w/h;camera.updateProjectionMatrix();
  renderer.setSize(w,h);composer.setSize(w,h);
}
const clk=new THREE.Clock();
function animate(){
  requestAnimationFrame(animate);
  ensureSize();
  // 基建拓扑变了才重建合并几何：一帧至多一次，与铺设次数无关
  flushInfrastructure();
  const dt=clk.getDelta(), t=performance.now(); acc+=dt;
  if(speed>0&&!shellPaused&&acc>.5){acc=0;K.advance(speed);shadowDirty();  // 太阳动了，阴影才需要重画
    const c=K.clock(); gameMin=c.minuteOfDay; onPowerChange(); onFluidChange(); onEconomyChange(); onPopulationChange();
    
    if(c.day!==day){day=c.day;dayEl.textContent=String(day);}
    clockEl.textContent=`${String(Math.floor(gameMin/60)).padStart(2,'0')}:${String(gameMin%60).padStart(2,'0')}`;}
  // 天穹/星空跟随相机：半径 7000 的球始终以相机为心，永远不会被 far 裁到
  skyGrp.position.copy(camera.position);
  updateDayNight(gameMin);
  beamOuter.material.opacity=.18+Math.sin(t*.0028)*.05;
  beamMid.material.opacity=.34+Math.sin(t*.0035)*.08;
  beamCore.material.opacity=.68+Math.sin(t*.004)*.16;
  beamCore.rotation.y=t*.0005; beamMid.rotation.y=-t*.0003;
  molten2.scale.setScalar(1+Math.sin(t*.003)*.06);
  molten3.scale.setScalar(1+Math.sin(t*.0045)*.1);
  coreGlow.intensity=2500+Math.sin(t*.004)*500;
  const pp=emberGeo.attributes.position;
  for(let i=0;i<emberN;i++){let y=pp.getY(i)+emberV[i];if(y>210)y=0;pp.setY(i,y);
    pp.setX(i,pp.getX(i)+Math.sin(t*.001+i)*.025);}
  pp.needsUpdate=true;
  dust.rotation.y=t*.00004;
  /* ---- 核心科幻动画 ---- */
  if(coreAnim.spinPivot){
    coreAnim.spinPivot.rotation.y=t*.0006;                 // 全息锁定环绕陨核扫描旋转
    coreAnim.spinPivot.rotation.x=Math.sin(t*.00021)*.12;  // 轻微进动
  }
  if(coreAnim.holo){                                        // 全息环发光脉冲 + 透明度呼吸
    coreAnim.holo.emissiveIntensity=3.2+Math.sin(t*.004)*1.6;
    coreAnim.holo.opacity=.5+Math.sin(t*.0026)*.18;
  }
  if(coreAnim.plasmaMat)                                     // 等离子导管/弧流动脉冲（能量奔流）
    coreAnim.plasmaMat.emissiveIntensity=6+Math.sin(t*.006)*2.4;
  if(coreAnim.cyanHiMat)                                     // 结构光缝轻微呼吸
    coreAnim.cyanHiMat.emissiveIntensity=4.4+Math.sin(t*.003)*1.1;
  if(coreAnim.coreMat)                                       // 陨核炽橙呼吸（炉心跳动）
    coreAnim.coreMat.emissiveIntensity=8+Math.sin(t*.0033)*1.8;
  if(coreAnim.wellMat)                                       // 井口熔炉橙涌动
    coreAnim.wellMat.emissiveIntensity=3.8+Math.sin(t*.0042)*1.2;
  camUpdate(dt);
  updateLabels();
  updateTierSprites();      // 等级牌按距离淡出，避免全景被标牌淹没
  updateBeacons();          // 类型信标按缩放档位淡出，同理
  composer.render();
}
animate();
toast("欢迎来到 零号陨坑 · CRATER ZERO");
/* 实时读取而非快照：会话化之后播种发生在 initShell（模块末尾）里，
   模块初始化时 bid 还是 0，写死会让探针永远报 0。 */
Object.defineProperty(window,'__renderInfo',{
  get:()=>({buildings:bid,districts:districts.length}),configurable:true});
/* debug/test hook: project a building to screen coords + force-select */
window.__cz={
  city,core,camera,camCtl,building,THREE,
  renderer,composer,bloom,coreAnim,   // 供调试/预览强制渲染与动画采样
  screenPosOf(type: string){let found=null as AnyObj;city.traverse(o=>{if(found)return;const r=o.userData.pickRoot;
    if(r&&r.userData.bType===type)found=r;});
    if(!found&&type==='core')found=core;
    if(!found)return null;
    const v=new THREE.Vector3(); new THREE.Box3().setFromObject(found).getCenter(v); v.project(camera);
    return {x:(v.x*.5+.5)*innerWidth,y:(-v.y*.5+.5)*innerHeight,visible:v.z<1};},
  select(type: string){const p=this.screenPosOf(type);return p;},
  selected:()=>selBldg?selBldg.userData.bType:null,
  infoShown:()=>binfoEl.classList.contains('show'),
} as AnyObj;

/* ================= 入口：交给外壳状态机 ================= */
const _mode=new URLSearchParams(location.search).get('mode');
/* ---------- 禁止浏览器级缩放（缩放只归镜头） ----------
   三条路径都要堵，少一条就漏：

   1. iOS Safari 的 gesturestart/change/end —— 它**无视 user-scalable=no**，
      只有 preventDefault 才拦得住。这三个事件非标准，故用字符串注册。
   2. Ctrl+滚轮 —— 桌面浏览器的页面缩放，触控板双指捏合也会合成成它。
      注意不能拦普通滚轮：那是镜头的缩放输入。
   3. 300ms 内的第二次 tap —— 双击放大。用时间戳判定，比依赖 dblclick 早，
      dblclick 触发时页面已经缩过了。

   全部只在**非输入元素**上拦截，否则会连带影响将来可能出现的输入框。 */
function initZoomGuard(){
  const stop=(e:Event)=>{e.preventDefault();};
  for(const t of ['gesturestart','gesturechange','gestureend']){
    document.addEventListener(t,stop,{passive:false});
  }
  addEventListener('wheel',(e:WheelEvent)=>{
    if(e.ctrlKey)e.preventDefault();      // 只拦页面缩放，普通滚轮留给镜头
  },{passive:false});
  let lastTap=0;
  document.addEventListener('touchend',(e:TouchEvent)=>{
    const now=e.timeStamp;
    if(now-lastTap<300&&e.touches.length===0)e.preventDefault();
    lastTap=now;
  },{passive:false});
}
initZoomGuard();

initDrawers();      // 抽屉条只在窄视口可见，宽屏下惰性
initShell({
  teardownScene,
  rebuildScene,
  setPaused:(p:boolean)=>{ shellPaused=p; },
  captureView:()=>({focusX:camCtl.tFocus.x,focusZ:camCtl.tFocus.z,
    yaw:camCtl.tYaw,pitch:camCtl.tPitch,dist:camCtl.tDist}),
  restoreView:(v)=>{ if(!v)return;
    camCtl.tFocus.set(v.focusX,0,v.focusZ);
    camCtl.tYaw=v.yaw; camCtl.tPitch=v.pitch; camCtl.tDist=v.dist; },
  toast,
},{skipMenu:_mode==='demo'||_mode==='fresh', seedDemo:_mode==='demo'});
